import { PRODUCT_FEATURES, PRODUCT_NAME, type DimensionId } from "@benyishiguang/ui-foundation";
import { PhoneLogin } from "@/features/auth/PhoneLogin";
import { phoneAuth } from "@/lib/auth-client";
import { DIM, DIMENSIONS } from "@/features/growth/dimensions";
import { EmptyState } from "@/components/growth/EmptyState";
import { PracticeScreen } from "@/features/growth/screens/PracticeScreen";
import type { Practice } from "@/features/growth/types";
export type { Practice } from "@/features/growth/types";
export { PracticeScreen } from "@/features/growth/screens/PracticeScreen";
import { BottomNav } from "@/components/growth/BottomNav";
import { DimensionEvidence } from "@/components/growth/DimensionEvidence";
import { ProfileSummary } from "@/components/growth/ProfileSummary";
import { KeywordCard as SharedKeywordCard } from "@/components/growth/KeywordCard";
import { growthTokenStyle as FOUNDATION_STYLE } from "@/styles/growth-tokens";
export { BottomNav } from "@/components/growth/BottomNav";
export type { DimensionId } from "@benyishiguang/ui-foundation";
import {
	ArrowLeft,
	ArrowRight,
	BadgeCheck,
	BookOpen,
	Bookmark,
	Check,
	ClipboardList,
	HandHeart,
	Image as ImageIcon,
	LockKeyhole,
	Mic,
	Pause,
	PenLine,
	Play,
	Sparkles,
	Star,
	SunMedium,
	Trophy,
	Type,
	User,
	UsersRound,
	X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import {
	confirmDailyKeyword,
	createStateEntry,
	createGroupReaction,
	createNote,
	createPracticeFeedback,
	createPracticeRecord,
	createRelationAsset,
	drawGrowthPractice,
	getGrowthBootstrap,
	growthBootstrapCacheKey,
	joinGrowthGroup,
	toggleOngoingPractice,
	type ApiBadge,
	type ApiEvidenceRule,
	type ApiEvidenceExplanation,
	type ApiGroup,
	type ApiGrowthReport,
	type ApiOngoingPractice,
	type ApiPractice,
	type ApiRelationAsset,
	type ApiSubskill,
	type ApiStateEncouragement,
	type ApiTimelineItem,
} from "@/api/growth";
import { clearUserSession, loadUserSession, type UserSession } from "@/lib/auth-session";
import { resolveMedia, uploadMediaReference } from "@/api/media";
import "./Home.css";
import "@/styles/growth.css";

type Tab = "home" | "practice" | "records" | "mine";
type Flow = "draw" | "detail" | "note" | null;
type RecordMode = "text" | "image" | "voice";
type NoteVisibility = "private" | "group" | "friend";
type GroupReactionType = "seen" | "support";
type GroupReactionDirection = "decrement" | "increment";

const isDevBuild = import.meta.env.DEV;
const GENERIC_API_ERROR_MESSAGE = "服务暂时不可用，请稍后再试。";
const DRAW_ERROR_MESSAGE = "今天的练习暂时没有准备好，请稍后再试。";
const DRAW_DEV_HELP_TEXT = "请确认 services/api 已启动，然后再试一次。";
const RAW_HTTP_ERROR_PATTERN = /^Request failed with status code \d+$/i;
const LOCAL_DEV_HINT_PATTERN = /services\/api|Node\/Postgres API|只启动 Vite|后端接口没有启动/u;

export type Ongoing = {
	id: string;
	practiceId: string;
	days: number;
	week: number[];
	today: number;
	checked: boolean;
};

export type TimelineItem = {
	id: string;
	kind: "practice" | "note";
	authorName?: string;
	dim: DimensionId | null;
	exerciseId?: string;
	feedbackScore?: number | null;
	friendName?: string | null;
	friendTag?: string | null;
	title?: string;
	time: string;
	text: string;
	image: string | null;
	reactions?: {
		seen: number;
		support: number;
	};
	myReactions?: {
		seen: boolean;
		support: boolean;
	};
	targetGroupId?: string | null;
	targetGroupName?: string | null;
	visibility?: NoteVisibility;
	voice: number;
	voiceAudio?: string | null;
};

export type Badge = {
	id: string;
	name: string;
	Icon: LucideIcon;
	got: boolean;
	dim: DimensionId;
	desc: string;
};

export type RelationAsset = {
	id: string;
	name: string;
	tag: string;
	from: string;
	time: string;
	sourceKind?: "practice" | "note";
	sourceNoteId?: string | null;
	sourceRecordId?: string | null;
	visibility?: string;
};

export type GrowthGroup = {
	description: string;
	dim: DimensionId;
	id: string;
	joined: boolean;
	memberCount: number;
	name: string;
	sessionLabel: string;
	sessionTitle: string;
};

export type ProfileStats = {
	avatar?: string;
	avTxt: string;
	joined: string;
	name: string;
	steadyDays: number;
	totalPractice: number;
	wellbeing: number;
	mediaCount: number;
	notesCount: number;
	todayCompleted: number;
};

export type GrowthReport = {
	focusDimension: DimensionId;
	headline: string;
	nextDecision: string;
	ranges: Array<{
		days: number;
		dimensions: Array<{ dimensionId: DimensionId; score: number }>;
		total: number;
	}>;
};

type SuccessState = {
	entry: TimelineItem;
	evidenceExplanation?: ApiEvidenceExplanation;
	evidenceScore?: number;
	nextAction?: string;
	nextSuggestion?: string;
	practice: Practice;
	recordId?: string;
};

export type PracticePayload = {
	mode: RecordMode;
	text: string;
	friendName?: string;
	friendTag?: string;
	image?: string | null;
	targetGroupId?: string | null;
	visibility?: NoteVisibility;
	voice?: number;
	voiceAudio?: string | null;
};

const cn = (...items: Array<string | false | null | undefined>) => items.filter(Boolean).join(" ");

const PRACTICE_ART_SRC = "/brand/practice-art.png";
const BRAND_ART_SRC = "/brand/brand-art.png";

const blurActiveControl = () => {
	const active = document.activeElement;
	if (!(active instanceof HTMLElement)) return;
	if (!active.matches("input, textarea, select, [contenteditable='true']")) return;
	active.blur();
	window.setTimeout(() => window.scrollTo(0, 0), 0);
};

const focusEditableWithoutScroll = <T extends HTMLInputElement | HTMLTextAreaElement>(
	event: React.PointerEvent<T>,
) => {
	if (event.pointerType === "mouse") return;
	if (document.activeElement === event.currentTarget) return;
	event.preventDefault();
	event.currentTarget.focus({ preventScroll: true });
};

const focusEditableOnTouchWithoutScroll = <T extends HTMLInputElement | HTMLTextAreaElement>(
	event: React.TouchEvent<T>,
) => {
	if (document.activeElement === event.currentTarget) return;
	event.preventDefault();
	event.currentTarget.focus({ preventScroll: true });
};

const IMAGE_ATTACHMENT_TARGET_BYTES = 72 * 1024;
const IMAGE_ATTACHMENT_MAX_EDGE = 720;
const IMAGE_ATTACHMENT_MIN_EDGE = 320;
const IMAGE_ATTACHMENT_QUALITIES = [0.74, 0.62, 0.5, 0.4, 0.32];

type PreparedImageAttachment = {
	dataUrl: string;
	status: string;
};

const formatByteSize = (bytes: number) => {
	if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const image = new Image();
		image.addEventListener("load", () => resolve(image), { once: true });
		image.addEventListener("error", () => reject(new Error("图片读取失败")), { once: true });
		image.src = src;
	});

const canvasToJpegDataUrl = (canvas: HTMLCanvasElement, quality: number): Promise<string> =>
	new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (!blob) {
					reject(new Error("图片压缩失败"));
					return;
				}
				const reader = new FileReader();
				reader.addEventListener("load", () => {
					if (typeof reader.result === "string") {
						resolve(reader.result);
						return;
					}
					reject(new Error("图片读取失败"));
				});
				reader.addEventListener("error", () => reject(new Error("图片读取失败")));
				reader.readAsDataURL(blob);
			},
			"image/jpeg",
			quality,
		);
	});

const drawImageToCanvas = (image: HTMLImageElement, maxEdge: number) => {
	const sourceWidth = image.naturalWidth;
	const sourceHeight = image.naturalHeight;
	if (!sourceWidth || !sourceHeight) throw new Error("图片读取失败");
	const scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
	const width = Math.max(1, Math.round(sourceWidth * scale));
	const height = Math.max(1, Math.round(sourceHeight * scale));
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext("2d");
	if (!context) throw new Error("当前浏览器不支持图片压缩");
	context.fillStyle = "#fff";
	context.fillRect(0, 0, width, height);
	context.drawImage(image, 0, 0, width, height);
	return canvas;
};

const prepareImageAttachment = async (file: File): Promise<PreparedImageAttachment> => {
	const objectUrl = URL.createObjectURL(file);
	try {
		const image = await loadImageElement(objectUrl);
		const originalEdge = Math.max(image.naturalWidth, image.naturalHeight);
		let edge = Math.min(IMAGE_ATTACHMENT_MAX_EDGE, originalEdge);
		let smallestDataUrl = "";

		while (edge >= IMAGE_ATTACHMENT_MIN_EDGE) {
			const canvas = drawImageToCanvas(image, edge);
			for (const quality of IMAGE_ATTACHMENT_QUALITIES) {
				const dataUrl = await canvasToJpegDataUrl(canvas, quality);
				smallestDataUrl = dataUrl;
				if (dataUrl.length <= IMAGE_ATTACHMENT_TARGET_BYTES) {
					return {
						dataUrl,
						status: `已压缩到 ${formatByteSize(dataUrl.length)}`,
					};
				}
			}
			edge = Math.floor(edge * 0.8);
		}

		if (smallestDataUrl && smallestDataUrl.length <= IMAGE_ATTACHMENT_TARGET_BYTES * 1.2) {
			return {
				dataUrl: smallestDataUrl,
				status: `已压缩到 ${formatByteSize(smallestDataUrl.length)}`,
			};
		}
		throw new Error("图片仍然过大，请换一张或裁小一点");
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
};

const isRenderableImageSrc = (value: string) => /^(data:image\/|blob:|https?:\/\/|\/)/u.test(value);

const mediaUrlCache = new Map<string, { expiresAt: number; url: string }>();
const useResolvedMedia = (reference?: string | null) => {
	const immediate = reference && !reference.startsWith("uploads/") ? reference : null;
	const cached = reference ? mediaUrlCache.get(reference) : undefined;
	const [url, setUrl] = useState<string | null>(
		cached && cached.expiresAt > Date.now() + 30_000 ? cached.url : immediate,
	);
	useEffect(() => {
		let active = true;
		let refresh: number | undefined;
		if (!reference) {
			setUrl(null);
			return () => {
				active = false;
			};
		}
		const freshCached = mediaUrlCache.get(reference);
		setUrl(
			freshCached && freshCached.expiresAt > Date.now() + 30_000
				? freshCached.url
				: reference.startsWith("uploads/")
					? null
					: reference,
		);
		const load = async () => {
			try {
				const resolved = await resolveMedia(reference);
				const expiresAt = resolved.expiresAt
					? Date.parse(resolved.expiresAt)
					: Number.MAX_SAFE_INTEGER;
				mediaUrlCache.set(reference, { expiresAt, url: resolved.url });
				if (!active) return;
				setUrl(resolved.url);
				if (Number.isFinite(expiresAt) && expiresAt < Number.MAX_SAFE_INTEGER) {
					refresh = window.setTimeout(
						() => void load(),
						Math.max(30_000, expiresAt - Date.now() - 30_000),
					);
				}
			} catch {
				if (active) setUrl(null);
			}
		};
		void load();
		return () => {
			active = false;
			if (refresh) window.clearTimeout(refresh);
		};
	}, [reference]);
	return url;
};

const TimelineImage: React.FC<{ source: string }> = ({ source }) => {
	const url = useResolvedMedia(source);
	return (
		<div className={cn("timeline-image", Boolean(url && isRenderableImageSrc(url)) && "has-image")}>
			{url && isRenderableImageSrc(url) && <img alt="记录图片" src={url} />}
		</div>
	);
};

const USER_INFO = {
	name: "练习者",
	avTxt: "练",
	joined: "正在同步成长档案",
	steadyDays: 0,
	totalPractice: 0,
	wellbeing: 0,
};

const getPublicProfileName = (session?: UserSession | null) => {
	const candidates = [session?.nickname, session?.name, session?.username]
		.map((item) => item?.trim())
		.filter(Boolean);
	const name = candidates.find((item) => item !== USER_INFO.name);
	if (name) return name;
	return USER_INFO.name;
};

// 成长档案 counts shown on 我的 (PDF).
const GROWTH_LEDGER: Array<{ dim: DimensionId; label: string; unit: string }> = [
	{ dim: "aware", label: "积极事件", unit: "条" },
	{ dim: "strength", label: "优势行动", unit: "次" },
	{ dim: "relate", label: "关系行动", unit: "次" },
	{ dim: "resil", label: "韧性练习", unit: "次" },
	{ dim: "hope", label: "希望行动", unit: "次" },
];

const SUMMARY_WORDS = ["温暖", "平静", "被看见", "松了口气"];
const RELATION_TAGS = ["朋友", "家人", "同事", "伴侣", "老师"];

const isDimensionId = (value: string): value is DimensionId => value in DIM;

const createEmptyDimensionScoreValues = () =>
	DIMENSIONS.reduce(
		(acc, dimension) => {
			acc[dimension.id] = 0;
			return acc;
		},
		{} as Record<DimensionId, number>,
	);

const mapPracticeDimensionScores = (practice: ApiPractice, primaryDim: DimensionId) => {
	const scores = (practice.dimensionScores ?? [])
		.filter((item) => isDimensionId(item.dimensionId) && Number(item.normalizedWeight) > 0)
		.map((item) => ({
			dim: item.dimensionId as DimensionId,
			isPrimary: item.dimensionId === primaryDim,
			source: item.source,
			weight: Number(item.normalizedWeight),
		}))
		.sort((a, b) => b.weight - a.weight);

	if (scores.some((item) => item.dim === primaryDim)) return scores;
	return [{ dim: primaryDim, isPrimary: true, source: "fallback", weight: 1 }, ...scores];
};

const cleanPracticeCopy = (value: string) =>
	value.replaceAll("打卡", "记录").replaceAll("连续记录可视化", "形成练习证据");

type PracticeActionDesign = {
	recordPrompt: string;
	steps: string[];
};

const splitPracticeFragments = (value: string) =>
	cleanPracticeCopy(value)
		.split(/[；;。，]/u)
		.map((item) => item.trim())
		.filter(Boolean);

const makePracticeSearchText = (practice: ApiPractice) =>
	`${practice.title} ${practice.actionStrategy} ${practice.originalTheme} ${practice.formTag}`;

const DIMENSION_ACTION_DESIGN: Record<DimensionId, PracticeActionDesign> = {
	aware: {
		steps: [
			"选一个今天真实发生的小片段",
			"写下当时让你有感觉的细节",
			"停留 20 秒，标出它让你好在哪一点",
		],
		recordPrompt: "可以按三句写：发生了什么；哪个细节被我注意到；它让我多了一点什么。",
	},
	strength: {
		steps: [
			"选一件你刚做过或做得还不错的小事",
			"找出里面用到的一种优势或能力",
			"想一个明天能再次使用它的场景",
		],
		recordPrompt: "可以按三句写：我做了什么；我用到了什么优势；下次我准备怎么继续用它。",
	},
	relate: {
		steps: ["选一个今天想靠近一点的人", "做一个很小的表达、倾听或回应", "观察对方或自己的反应变化"],
		recordPrompt: "可以按三句写：我对谁做了什么；对方或我有什么反应；这让我更理解了什么关系需要。",
	},
	resil: {
		steps: [
			"写下一个今天真实存在的困难或情绪",
			"给它命名，并区分可控与不可控部分",
			"选择一个现在能做的支持动作",
		],
		recordPrompt:
			"可以按三句写：困难是什么；我现在的情绪叫什么；我下一步能照顾自己的一个动作是什么。",
	},
	hope: {
		steps: [
			"选一个你在意的小目标",
			"写出一条可能走得通的路径",
			"把路径缩成今天能完成的 10 分钟动作",
		],
		recordPrompt: "可以按三句写：我想靠近什么；有哪条路径；今天我先做哪一个最小动作。",
	},
};

const buildPracticeActionDesign = (practice: ApiPractice): PracticeActionDesign => {
	const text = makePracticeSearchText(practice);
	const fallback = isDimensionId(practice.dimensionId)
		? DIMENSION_ACTION_DESIGN[practice.dimensionId]
		: DIMENSION_ACTION_DESIGN.aware;
	const fragments = splitPracticeFragments(practice.actionStrategy);
	const firstAction = fragments[0] || practice.title;

	if (/三件好事|三件趣事|好事|趣事|感恩|感谢|品味|美好|积极事件/u.test(text)) {
		return {
			steps: [
				"回想今天已经发生的三件具体小事",
				"每件事写清楚时间、地点、人物或一个细节",
				"为其中一件补一句原因：它为什么会发生，或为什么值得留下",
			],
			recordPrompt: "可以按三行写：第一件事和原因；第二件事和原因；第三件事和原因。",
		};
	}

	if (/悲伤|落泪|哀伤|难过|失去|告别/u.test(text)) {
		return {
			steps: [
				"选一个让你难过的具体事件，不需要写得完整",
				"对照五阶段，标出此刻最接近的状态：否认、愤怒、祈求、抑郁或接受",
				"允许自己用一种安全方式表达：写三句话、哭一会儿，或告诉可信任的人一句真实感受",
			],
			recordPrompt: "可以按三句写：发生了什么；我现在更像哪一阶段；我允许自己怎样照顾这份悲伤。",
		};
	}

	if (/呼吸|五感|正念|身体|扫描|冥想|此时此刻|着陆|感官/u.test(text)) {
		return {
			steps: [
				"停下来 60 秒，把注意力放回身体或环境",
				"依次记录一个看见、听见或感觉到的具体信号",
				"选一个让身体稍微放松的动作，再做 3 次呼吸",
			],
			recordPrompt:
				"可以按三句写：我注意到了什么身体或环境信号；它让我有什么变化；我做了哪个放松动作。",
		};
	}

	if (/优势|长处|擅长|最好|最佳|高光|成功|自我效能|VIA/u.test(text)) {
		return {
			steps: [
				"回想一件你做得比较顺的真实小事",
				"从这件事里圈出一个你用到的优势或做法",
				"写下明天可以在哪个小场景复用它",
			],
			recordPrompt: "可以按三句写：我做顺了什么；我用了什么优势；明天我打算把它用在哪里。",
		};
	}

	if (/未来|希望|目标|路径|计划|一小步|可能自我|愿望|成长型/u.test(text)) {
		return {
			steps: [
				"选一个你希望慢慢靠近的方向",
				"写出一幅更具体的未来画面，包含一个生活细节",
				"拆出今天能做的一小步，控制在 10 分钟以内",
			],
			recordPrompt: "可以按三句写：我想靠近的未来是什么；画面里有什么细节；今天我先做哪一步。",
		};
	}

	if (/关系|朋友|家人|同事|伴侣|倾听|回应|联结|分享|赞美|原谅|沟通|求助/u.test(text)) {
		return {
			steps: [
				"选一个你今天想认真对待的人",
				"做一个具体关系动作：发一句感谢、认真听完，或表达一个需要",
				"记录这次互动后，你对这段关系多看见了什么",
			],
			recordPrompt:
				"可以按三句写：我对谁做了什么；对方或我有什么变化；这段关系里我看见了什么需要。",
		};
	}

	if (/压力|困难|失败|挫折|担心|焦虑|愤怒|情绪|自我关怀|打气|安慰|复盘|接纳/u.test(text)) {
		return {
			steps: [
				"写下一个今天真实让你卡住的点",
				"把它拆成可控和暂时不可控两部分",
				"给自己一句支持，并选择一个可执行的下一步",
			],
			recordPrompt: "可以按三句写：我卡在哪里；哪些能控制、哪些先放下；我下一步做什么来支持自己。",
		};
	}

	if (/问卷|量表|测评|自查/u.test(text)) {
		return {
			steps: [
				"按直觉完成自查，不追求答案好看",
				"选出一个分数最高或最低的项目",
				"写下它今天对应的一个真实行为证据",
			],
			recordPrompt: "可以按三句写：我选中的项目是什么；今天有什么证据；我想保留或调整哪一点。",
		};
	}

	if (fragments.length >= 2) {
		return {
			steps: [firstAction, fragments[1], fallback.steps[2]],
			recordPrompt: fallback.recordPrompt,
		};
	}

	if (fragments.length === 1) {
		return {
			steps: [firstAction, fallback.steps[1], fallback.steps[2]],
			recordPrompt: fallback.recordPrompt,
		};
	}

	return fallback;
};

const parseStoredPracticeSteps = (value?: string) => {
	if (!value) return [];
	try {
		const parsed = JSON.parse(value) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(
			(item): item is string => typeof item === "string" && item.trim().length > 0,
		);
	} catch {
		return [];
	}
};

const mapApiPractice = (practice: ApiPractice): Practice | null => {
	const displayDimension = practice.displayDimensionId ?? practice.dimensionId;
	if (!isDimensionId(displayDimension)) return null;
	const minutes = practice.estimatedMinutes || 3;
	const form = practice.formTag || "行动";
	const hasAssessment = /测评|量表|问卷|VIA/u.test(`${practice.title} ${practice.actionStrategy}`);
	const fallbackActionDesign = buildPracticeActionDesign(practice);
	const storedSteps = parseStoredPracticeSteps(practice.actionStepsJson);
	const actionDesign = {
		recordPrompt: practice.recordPrompt?.trim() || fallbackActionDesign.recordPrompt,
		steps: storedSteps.length ? storedSteps : fallbackActionDesign.steps,
	};

	return {
		id: practice.id,
		dim: displayDimension,
		directoryFeatured: Boolean(practice.isDirectoryFeatured),
		directoryRank: practice.directoryRank ?? null,
		title: practice.title,
		sub: `${minutes} 分钟 · ${form}`,
		intro: cleanPracticeCopy(practice.actionStrategy),
		dimensionScores: mapPracticeDimensionScores(practice, displayDimension),
		share: Boolean(practice.isShareable),
		course: {
			title: practice.theorySource ? `方法来源：${practice.theorySource}` : "练习方法说明",
			len: practice.originalTheme || "积极心理学练习",
		},
		assess: hasAssessment ? { title: `${practice.title}评估`, q: 6 } : null,
		source: {
			appSurface: practice.appSurface,
			formTag: practice.formTag,
			originalTheme: practice.originalTheme,
			socialTag: practice.socialTag,
			theorySource: practice.theorySource,
		},
		steps: actionDesign.steps,
		recordPrompt: actionDesign.recordPrompt,
	};
};

const mapApiPractices = (practices: ApiPractice[] | undefined) =>
	(practices ?? [])
		.map(mapApiPractice)
		.filter((practice): practice is Practice => Boolean(practice));

const BADGE_ICONS: Record<string, LucideIcon> = {
	"badge-check": BadgeCheck,
	"heart-hand": HandHeart,
	star: Star,
	sun: SunMedium,
	trophy: Trophy,
};

export const BADGE_CATALOG: Badge[] = [
	{
		id: "badge-first",
		Icon: BadgeCheck,
		desc: "完成第一次练习",
		dim: "aware",
		got: false,
		name: "初次练习",
	},
	{
		id: "badge-week",
		Icon: SunMedium,
		desc: "累计完成 7 次真实行动证据",
		dim: "strength",
		got: false,
		name: "一周稳定练习",
	},
	{
		id: "badge-relation",
		Icon: HandHeart,
		desc: "完成一次关系分享或关系记录",
		dim: "relate",
		got: false,
		name: "一次真诚联结",
	},
	{
		id: "badge-hope",
		Icon: Star,
		desc: "完成 10 次希望行动",
		dim: "hope",
		got: false,
		name: "希望行动者",
	},
	{
		id: "badge-resil",
		Icon: Trophy,
		desc: "完成 12 次韧性调适练习",
		dim: "resil",
		got: false,
		name: "韧性练习章",
	},
];

const mapApiBadges = (badges: ApiBadge[] | undefined): Badge[] =>
	(badges ?? []).map((badge) => ({
		id: badge.id,
		Icon: BADGE_ICONS[badge.icon] ?? BadgeCheck,
		desc: badge.description,
		dim: isDimensionId(badge.dimensionId) ? badge.dimensionId : "aware",
		got: Boolean(badge.unlockedAt),
		name: badge.name,
	}));

const mergeApiBadges = (badges: ApiBadge[] | undefined): Badge[] => {
	const remote = mapApiBadges(badges);
	const remoteMap = new Map(remote.map((badge) => [badge.id, badge]));
	const catalogIds = new Set(BADGE_CATALOG.map((badge) => badge.id));
	return [
		...BADGE_CATALOG.map((badge) => remoteMap.get(badge.id) ?? badge),
		...remote.filter((badge) => !catalogIds.has(badge.id)),
	];
};

const mapApiTimeline = (items: ApiTimelineItem[] | undefined): TimelineItem[] =>
	(items ?? []).map((item) => ({
		id: item.id,
		authorName: item.authorName,
		dim: item.dimensionId && isDimensionId(item.dimensionId) ? item.dimensionId : null,
		exerciseId: item.exerciseId,
		feedbackScore: item.feedbackScore,
		friendName: item.friendName,
		friendTag: item.friendTag,
		image: item.image,
		kind: item.kind,
		myReactions: item.myReactions,
		reactions: item.reactions,
		targetGroupId: item.targetGroupId,
		targetGroupName: item.targetGroupName,
		text: item.text,
		time: item.time,
		title: item.title,
		visibility: item.visibility,
		voice: item.voice,
		voiceAudio: item.audioKey,
	}));

const mapApiRelations = (items: ApiRelationAsset[] | undefined): RelationAsset[] =>
	(items ?? []).map((item) => ({
		id: item.id,
		from: item.sourceTitle || "关系行动",
		name: item.name,
		sourceKind: item.sourceKind,
		sourceNoteId: item.sourceNoteId,
		sourceRecordId: item.sourceRecordId,
		tag: item.tag,
		time: "刚刚",
		visibility: item.visibility,
	}));

const mapApiOngoing = (items: ApiOngoingPractice[] | undefined): Ongoing[] =>
	(items ?? []).map((item) => ({
		id: item.id,
		checked: item.checked,
		days: item.days,
		practiceId: item.exerciseId,
		today: item.todayIndex,
		week: item.week.length === 7 ? item.week : [0, 0, 0, 0, 0, 0, 0],
	}));

const mapApiGroups = (items: ApiGroup[] | undefined): GrowthGroup[] =>
	(items ?? [])
		.filter((item) => item.dimensionId && isDimensionId(item.dimensionId))
		.map((item) => ({
			id: item.id,
			description: item.description,
			dim: item.dimensionId as DimensionId,
			joined: item.joined,
			memberCount: item.memberCount,
			name: item.name,
			sessionLabel: item.session?.scheduledLabel ?? "本周",
			sessionTitle: item.session?.title ?? "共同练习",
		}));

const buildEmptyReport = (): GrowthReport => ({
	focusDimension: "aware",
	headline: "报告还在等待证据",
	nextDecision: "完成几次真实练习后，这里会给出最近变化和下一步建议。",
	ranges: [
		{ days: 7, dimensions: [], total: 0 },
		{ days: 28, dimensions: [], total: 0 },
		{ days: 90, dimensions: [], total: 0 },
	],
});

const mapApiReport = (report: ApiGrowthReport | undefined): GrowthReport =>
	report && isDimensionId(report.focusDimension)
		? {
				focusDimension: report.focusDimension,
				headline: report.headline,
				nextDecision: report.nextDecision,
				ranges: report.ranges.map((range) => ({
					...range,
					dimensions: range.dimensions.flatMap((item) =>
						isDimensionId(item.dimensionId)
							? [
									{
										dimensionId: item.dimensionId,
										score: Number(item.score ?? item.count ?? 0),
									},
								]
							: [],
					),
				})),
			}
		: buildEmptyReport();

const mapApiDimensionScoreValues = (
	items: Array<{ dimensionId: string; scoreTotal?: number }> | undefined,
) => {
	const values = createEmptyDimensionScoreValues();
	for (const item of items ?? []) {
		if (isDimensionId(item.dimensionId)) {
			values[item.dimensionId] = Number(item.scoreTotal ?? 0);
		}
	}
	return values;
};

const buildPracticeMap = (practices: Practice[]) =>
	Object.fromEntries(practices.map((practice) => [practice.id, practice])) as Record<
		string,
		Practice
	>;

const getUserFacingApiMessage = (
	message: string | undefined,
	fallback = GENERIC_API_ERROR_MESSAGE,
) => {
	const trimmed = message?.trim();
	if (!trimmed) return fallback;
	if (RAW_HTTP_ERROR_PATTERN.test(trimmed)) return fallback;
	if (!isDevBuild && LOCAL_DEV_HINT_PATTERN.test(trimmed)) return fallback;
	return trimmed;
};

const estimateEvidenceScore = (payload: PracticePayload, keyword: string) => {
	const text = payload.text.trim();
	let score = 1;
	if (text.length >= 8) score += 1;
	if (text.length >= 24) score += 1;
	if (/[，。；、,.!?！？]/u.test(text) || /因为|所以|感受|发现|下一步|具体/u.test(text)) {
		score += 1;
	}
	if (keyword.trim()) score += 1;
	if (payload.mode === "image" && payload.image) score += 1;
	if (payload.mode === "voice" && (payload.voice ?? 0) >= 8) score += 1;
	return Math.max(1, Math.min(5, score));
};

const getApiError = (error: unknown, fallback = GENERIC_API_ERROR_MESSAGE) => {
	if (typeof error !== "object" || !error || !("code" in error)) return null;
	const candidate = error as { code?: unknown; message?: unknown };
	return {
		code: typeof candidate.code === "number" ? candidate.code : 500,
		message: getUserFacingApiMessage(
			typeof candidate.message === "string" ? candidate.message : undefined,
			fallback,
		),
	};
};

export const Home: React.FC = () => {
	const isAuthCallback = false;
	const [tab, setTab] = useState<Tab>("home");
	const [flow, setFlow] = useState<Flow>(null);
	const [authSession, setAuthSession] = useState<UserSession | null>(() => loadUserSession());
	const [authLoading, setAuthLoading] = useState(false);
	const pendingAuthActionRef = useRef<(() => void | Promise<void>) | null>(null);
	const authRedirectStartedRef = useRef(false);
	const authRecoveryRef = useRef(false);
	const refreshedAuthProfileTokenRef = useRef<string | null>(null);
	const [selectedPractice, setSelectedPractice] = useState<Practice | null>(null);
	const [recordFor, setRecordFor] = useState<Practice | null>(null);
	const [timeline, setTimeline] = useState<TimelineItem[]>([]);
	const [groupTimeline, setGroupTimeline] = useState<TimelineItem[]>([]);
	const [ongoing, setOngoing] = useState<Ongoing[]>([]);
	const [openingLine, setOpeningLine] = useState("");
	const [openingConfirmed, setOpeningConfirmed] = useState(false);
	const [openingSaving, setOpeningSaving] = useState(false);
	const [stateEncouragement, setStateEncouragement] = useState<ApiStateEncouragement | null>(null);
	const [todayPractice, setTodayPractice] = useState<Practice | null>(null);
	const [todayCompleted, setTodayCompleted] = useState(false);
	const [todayKeyword, setTodayKeyword] = useState("");
	const [keywordConfirmed, setKeywordConfirmed] = useState(false);
	const [toast, setToast] = useState("");
	const [success, setSuccess] = useState<SuccessState | null>(null);
	const [shareFor, setShareFor] = useState<TimelineItem | null>(null);
	const [relations, setRelations] = useState<RelationAsset[]>([]);
	const [badges, setBadges] = useState<Badge[]>(BADGE_CATALOG);
	const [groups, setGroups] = useState<GrowthGroup[]>([]);
	const {
		data: bootstrap,
		error: bootstrapError,
		isLoading: bootstrapLoading,
		mutate: refreshBootstrap,
	} = useSWR(
		authSession ? growthBootstrapCacheKey(authSession.id) : null,
		() => getGrowthBootstrap(),
		{
			shouldRetryOnError: false,
		},
	);
	const apiPractices = useMemo(() => mapApiPractices(bootstrap?.data.practices), [bootstrap]);
	const practices = apiPractices;
	const practiceMap = useMemo(() => buildPracticeMap(practices), [practices]);
	const practiceLoadError = bootstrapError
		? (getApiError(bootstrapError)?.message ?? "数据库题库加载失败，请检查后端接口")
		: null;
	const subskills = bootstrap?.data.subskills ?? [];
	const evidenceRules = bootstrap?.data.evidenceRules ?? [];
	const publicProfileName = getPublicProfileName(authSession);
	const profile: ProfileStats = {
		...USER_INFO,
		...bootstrap?.data.profile,
		avatar: authSession?.avatar,
		avTxt: publicProfileName.slice(0, 1) || USER_INFO.avTxt,
		joined: authSession ? "已登录 · 成长数据已同步" : "登录后同步你的成长档案",
		mediaCount: bootstrap?.data.profile?.mediaCount ?? 0,
		name: publicProfileName,
		notesCount: bootstrap?.data.profile?.notesCount ?? 0,
		todayCompleted: bootstrap?.data.profile?.todayCompleted ?? 0,
	};
	const report = mapApiReport(bootstrap?.data.report);
	const dimensionScoreValues = mapApiDimensionScoreValues(bootstrap?.data.dimensionScores);
	useEffect(() => {
		if (!bootstrap?.data) return;
		const nextTimeline = mapApiTimeline(bootstrap.data.timeline);
		setTimeline(nextTimeline);
		setGroupTimeline(mapApiTimeline(bootstrap.data.groupTimeline));
		const nextRelations = mapApiRelations(bootstrap.data.relations);
		setRelations(nextRelations);
		const nextBadges = mergeApiBadges(bootstrap.data.badges);
		setBadges(nextBadges);
		const nextGroups = mapApiGroups(bootstrap.data.groups);
		setGroups(nextGroups);
		const nextOngoing = mapApiOngoing(bootstrap.data.ongoing);
		setOngoing(nextOngoing);
		if (bootstrap.data.dailyKeyword) {
			setTodayKeyword(bootstrap.data.dailyKeyword.keyword);
			setKeywordConfirmed(true);
		} else {
			setKeywordConfirmed(false);
		}
		if (bootstrap.data.currentStateEntry) {
			setOpeningLine(bootstrap.data.currentStateEntry.text);
			setOpeningConfirmed(true);
			setStateEncouragement(bootstrap.data.currentStateEntry.encouragement);
		} else {
			const drawOpening = bootstrap.data.todayDraw?.openingLine ?? "";
			setOpeningLine(drawOpening);
			setOpeningConfirmed(false);
			setStateEncouragement(null);
		}
		if (bootstrap.data.todayDraw) {
			const drawnPractice = mapApiPractice(bootstrap.data.todayDraw.practice);
			if (drawnPractice) {
				setTodayPractice(drawnPractice);
			}
			setTodayCompleted(bootstrap.data.todayDraw.completed);
		} else {
			setTodayPractice(null);
			setTodayCompleted(false);
		}
	}, [bootstrap]);

	useEffect(() => {
		if (!toast) return undefined;
		const timer = window.setTimeout(() => setToast(""), 1800);
		return () => window.clearTimeout(timer);
	}, [toast]);

	const showToast = useCallback((message: string) => setToast(message), []);

	// A cached login token can outlive the API session. Treat the API's 401 as
	// an auth boundary, otherwise the practice catalogue remains stuck on the
	// misleading "database failed" state forever.
	useEffect(() => {
		if (getApiError(bootstrapError)?.code !== 401 || authRecoveryRef.current) return;
		authRecoveryRef.current = true;
		authRedirectStartedRef.current = false;
		refreshedAuthProfileTokenRef.current = null;
		clearUserSession();
		setAuthSession(null);
		setTimeline([]);
		setGroupTimeline([]);
		setOngoing([]);
		setTodayPractice(null);
		setTodayCompleted(false);
		showToast("登录状态已失效，请重新登录");
	}, [bootstrapError, showToast]);

	useEffect(() => {
		if (authSession) authRecoveryRef.current = false;
	}, [authSession]);

	const hasAuthSession = () => Boolean(authSession || loadUserSession());

	const startPhoneLogin = useCallback(async () => {
		setAuthLoading(false);
		setAuthSession(null);
	}, []);

	const requireAuth = (action?: () => void | Promise<void>) => {
		if (hasAuthSession()) return true;
		pendingAuthActionRef.current = action ?? null;
		void startPhoneLogin();
		return false;
	};

	const runProtected = (action: () => void | Promise<void>) => {
		if (requireAuth(action)) void action();
	};

	const confirmTodayKeyword = () => {
		const keyword = todayKeyword.trim();
		if (keywordConfirmed) {
			showToast("今日关键词已经确认");
			return;
		}
		if (!keyword) {
			showToast("先留一个词");
			return;
		}
		runProtected(async () => {
			try {
				const response = await confirmDailyKeyword({ keyword });
				setTodayKeyword(response.data.keyword);
				setKeywordConfirmed(true);
				void refreshBootstrap();
				showToast("今日关键词已确认");
			} catch (error) {
				showToast(getApiError(error)?.message ?? "今日关键词保存失败，请检查后端接口");
			}
		});
	};

	const confirmOpeningLine = () => {
		const opening = openingLine.trim();
		if (openingConfirmed && opening) {
			showToast("今日状态已经确认");
			return;
		}
		if (!opening) {
			setOpeningConfirmed(false);
			showToast("先写一句今日状态");
			return;
		}
		runProtected(async () => {
			setOpeningSaving(true);
			try {
				const response = await createStateEntry({ text: opening });
				setOpeningLine(response.data.text);
				setOpeningConfirmed(true);
				setStateEncouragement(response.data.encouragement);
				void refreshBootstrap();
				showToast("今日状态已记录");
			} catch (error) {
				setOpeningConfirmed(false);
				showToast(getApiError(error)?.message ?? "今日状态保存失败，请稍后重试");
			} finally {
				setOpeningSaving(false);
			}
		});
	};

	const openDrawFlow = () => {
		runProtected(() => {
			const opening = openingLine.trim();
			setOpeningLine(opening);
			setFlow("draw");
		});
	};

	const handleAuthLogout = async () => {
		try {
			if (authSession?.token) await phoneAuth.logout(authSession.token);
		} catch {
			showToast("退出失败，请检查网络后重试");
			return;
		}
		pendingAuthActionRef.current = null;
		await refreshBootstrap(undefined, { revalidate: false });
		clearUserSession();
		setAuthSession(null);
		setTimeline([]);
		setGroupTimeline([]);
		setOngoing([]);
		setOpeningLine("");
		setOpeningConfirmed(false);
		setStateEncouragement(null);
		setTodayPractice(null);
		setTodayCompleted(false);
		setTodayKeyword("");
		setKeywordConfirmed(false);
		setRelations([]);
		setBadges(BADGE_CATALOG);
		setGroups([]);
		setSuccess(null);
		setShareFor(null);
		showToast("已退出登录");
	};

	useEffect(() => {
		if (!authSession?.token || isAuthCallback) return;
		const shouldRefreshProfile =
			!authSession.avatar ||
			!authSession.nickname ||
			authSession.username ||
			authSession.name === USER_INFO.name;
		if (!shouldRefreshProfile || refreshedAuthProfileTokenRef.current === authSession.token) return;
		refreshedAuthProfileTokenRef.current = authSession.token;
		let cancelled = false;
		let pending = true;
		void import("@/lib/auth-client")
			.then(({ refreshUserSession }) => refreshUserSession(authSession))
			.then((session) => {
				if (cancelled) return;
				pending = false;
				setAuthSession(session);
			})
			.catch(() => {
				// Keep the session on transient network failures; business API 401 triggers the login gate.
			});
		return () => {
			cancelled = true;
			if (pending) refreshedAuthProfileTokenRef.current = null;
		};
	}, [authSession, isAuthCallback]);

	const openTab = (next: Tab) => {
		setTab(next);
		setFlow(null);
		setSelectedPractice(null);
		setRecordFor(null);
	};

	const openPractice = (practiceId?: string) => {
		if (!practiceId) {
			openTab("practice");
			return;
		}
		const practice = practiceMap[practiceId];
		if (!practice) {
			openTab("practice");
			return;
		}
		setSelectedPractice(practice);
		setFlow("detail");
	};

	const drawToday = async () => {
		if (todayPractice) return todayPractice;
		try {
			const response = await drawGrowthPractice({
				availableMinutes: 3,
				openingLine: openingLine.trim(),
			});
			const remotePractice = mapApiPractice(response.data.practice);
			if (!remotePractice) throw new Error("今日练习数据异常");
			setTodayPractice(remotePractice);
			setTodayCompleted(response.data.completed);
			setOpeningLine((current) => current || response.data.todayDraw.openingLine);
			void refreshBootstrap();
			return remotePractice;
		} catch (error) {
			showToast(getApiError(error)?.message ?? "今日翻卡失败，请检查后端接口");
			throw error;
		}
	};

	const toggleOngoing = async (practiceId: string) => {
		if (!requireAuth(() => toggleOngoing(practiceId))) return;
		const wasActive = ongoing.some((item) => item.practiceId === practiceId);
		const practiceTitle = practiceMap[practiceId]?.title ?? "这个练习";
		try {
			const response = await toggleOngoingPractice({ exerciseId: practiceId });
			await refreshBootstrap();
			showToast(
				response.data.active
					? `「${practiceTitle}」已加入持续练习`
					: `「${practiceTitle}」已从持续练习列表移除`,
			);
		} catch (error) {
			showToast(
				getApiError(error)?.message ??
					(wasActive
						? `「${practiceTitle}」移除失败，请检查后端接口`
						: `「${practiceTitle}」加入失败，请检查后端接口`),
			);
		}
	};

	const completePractice = async (practice: Practice, payload: PracticePayload) => {
		if (todayCompleted && todayPractice?.id === practice.id) {
			showToast("今天这个练习已完成，可在记录里查看");
			return;
		}
		if (!requireAuth(() => completePractice(practice, payload))) return;
		const localId = `r${Date.now()}`;
		const confirmedKeyword = keywordConfirmed ? todayKeyword : "";
		const localScore = estimateEvidenceScore(payload, confirmedKeyword);
		let recordId = localId;
		let evidenceExplanation: ApiEvidenceExplanation | undefined;
		let evidenceScore = localScore;
		let nextSuggestion =
			localScore >= 5
				? "这次记录很清楚，已经能看到你的行动、感受和线索。"
				: localScore <= 2
					? "这次记录还比较简短。多写一点做了什么、感受或原因，成长档案会更准。"
					: "这次记录已经可用。再补一点细节，成长档案会更准确。";
		let nextAction = localScore >= 5 ? "upgrade" : localScore <= 2 ? "switch" : "continue";
		let uploadedImageUrl: string | null = null;
		let uploadedAudioUrl: string | null = null;
		try {
			const image =
				payload.mode === "image" ? await uploadMediaReference(payload.image, "image") : null;
			const audio =
				payload.mode === "voice" ? await uploadMediaReference(payload.voiceAudio, "audio") : null;
			uploadedImageUrl = image?.url ?? null;
			uploadedAudioUrl = audio?.url ?? null;
			const response = await createPracticeRecord({
				audioKey: audio?.key,
				content: payload.text,
				exerciseId: practice.id,
				imageKey: image?.key,
				mode: payload.mode,
				summaryKeyword: confirmedKeyword,
				voiceSeconds: payload.voice,
			});
			recordId = response.data.id;
			evidenceExplanation = response.data.evidenceExplanation;
			evidenceScore = response.data.evidenceScore;
			nextSuggestion = response.data.nextSuggestion;
			nextAction = response.data.nextAction;
			setBadges(mergeApiBadges(response.data.badges));
		} catch (error) {
			showToast(getApiError(error)?.message ?? "练习保存失败，请检查后端接口");
			return;
		}
		const entry: TimelineItem = {
			id: recordId,
			kind: "practice",
			dim: practice.dim,
			exerciseId: practice.id,
			feedbackScore: evidenceScore,
			title: practice.title,
			time: "刚刚",
			text: payload.text || `完成了「${practice.title}」。今天先把这个动作做完，也算一次有效练习。`,
			image: payload.mode === "image" ? (uploadedImageUrl ?? payload.image ?? null) : null,
			voice: payload.mode === "voice" ? (payload.voice ?? 12) : 0,
			voiceAudio: payload.mode === "voice" ? (uploadedAudioUrl ?? payload.voiceAudio) : null,
		};
		setTimeline((items) => [entry, ...items]);
		setOngoing((items) => {
			const nextItems = items.map((item) =>
				item.practiceId === practice.id && !item.checked
					? {
							...item,
							checked: true,
							days: item.days + 1,
							week: item.week.map((value, index) => (index === item.today ? 1 : value)),
						}
					: item,
			);
			return nextItems;
		});
		if (todayPractice?.id === practice.id) {
			setTodayCompleted(true);
		}
		void refreshBootstrap();
		setRecordFor(null);
		setFlow(null);
		setSelectedPractice(null);
		setTab("home");
		setSuccess({
			entry,
			evidenceExplanation,
			evidenceScore,
			nextAction,
			nextSuggestion,
			practice,
			recordId,
		});
	};

	const publishNote = async (payload: PracticePayload) => {
		if (!requireAuth(() => publishNote(payload))) return;
		let noteId = `n${Date.now()}`;
		const visibility = payload.visibility ?? "private";
		const targetGroup = groups.find((group) => group.id === payload.targetGroupId) ?? null;
		let relationAsset: RelationAsset | null = null;
		let uploadedImageUrl: string | null = null;
		let uploadedAudioUrl: string | null = null;
		try {
			const image =
				payload.mode === "image" ? await uploadMediaReference(payload.image, "image") : null;
			const audio =
				payload.mode === "voice" ? await uploadMediaReference(payload.voiceAudio, "audio") : null;
			uploadedImageUrl = image?.url ?? null;
			uploadedAudioUrl = audio?.url ?? null;
			const response = await createNote({
				audioKey: audio?.key,
				authorName: profile.name,
				content: payload.text,
				friendName: payload.friendName,
				friendTag: payload.friendTag,
				imageKey: image?.key,
				mode: payload.mode,
				targetGroupId: payload.targetGroupId,
				visibility,
				voiceSeconds: payload.voice,
			});
			noteId = response.data.id;
			if (response.data.relationAsset) {
				relationAsset = mapApiRelations([response.data.relationAsset])[0];
			}
			if (response.data.badges?.length) {
				setBadges(mergeApiBadges(response.data.badges));
			}
		} catch (error) {
			showToast(getApiError(error)?.message ?? "随心记保存失败，请检查后端接口");
			return;
		}
		const entry: TimelineItem = {
			id: noteId,
			kind: "note",
			authorName: profile.name,
			dim: null,
			friendName: visibility === "friend" ? payload.friendName : null,
			friendTag: visibility === "friend" ? payload.friendTag : null,
			time: "刚刚",
			text: payload.text || "留下了一条没有分类的随心记录。",
			image: payload.mode === "image" ? (uploadedImageUrl ?? payload.image ?? null) : null,
			myReactions: visibility === "group" ? { seen: false, support: false } : undefined,
			reactions: visibility === "group" ? { seen: 0, support: 0 } : undefined,
			targetGroupId: visibility === "group" ? payload.targetGroupId : null,
			targetGroupName: visibility === "group" ? targetGroup?.name : null,
			visibility,
			voice: payload.mode === "voice" ? (payload.voice ?? 10) : 0,
			voiceAudio: payload.mode === "voice" ? (uploadedAudioUrl ?? payload.voiceAudio) : null,
		};
		setTimeline((items) => [entry, ...items]);
		if (visibility === "group") {
			setGroupTimeline((items) => [entry, ...items]);
		}
		if (visibility === "friend") {
			setRelations((items) => (relationAsset ? [relationAsset, ...items] : items));
		}
		setFlow(null);
		setTab("records");
		showToast(
			visibility === "group"
				? "已发到小组"
				: visibility === "friend"
					? "已生成朋友分享"
					: "随心记已保存",
		);
	};

	const addRelationAsset = async (asset: Omit<RelationAsset, "id" | "time" | "from">) => {
		if (!shareFor) return;
		if (!requireAuth(() => addRelationAsset(asset))) return;
		try {
			const response = await createRelationAsset({
				name: asset.name,
				sourceKind: "practice",
				sourceRecordId: shareFor.id,
				sourceTitle: shareFor.title ?? "随心记",
				tag: asset.tag,
				visibility: asset.visibility ?? "friend",
			});
			setRelations((items) => [mapApiRelations([response.data.asset])[0], ...items]);
			setBadges(mergeApiBadges(response.data.badges));
		} catch (error) {
			showToast(getApiError(error)?.message ?? "分享失败，请检查后端接口");
			return;
		}
		setTimeline((items) =>
			items.map((item) =>
				item.id === shareFor.id ? { ...item, friendName: asset.name, friendTag: asset.tag } : item,
			),
		);
		setShareFor(null);
		showToast(`已准备分享到 ${asset.name}`);
	};

	const submitFeedback = async (score: number, reason: string) => {
		if (!success?.recordId) return;
		if (!requireAuth(() => submitFeedback(score, reason))) return;
		try {
			const response = await createPracticeFeedback({
				exerciseId: success.practice.id,
				reason,
				recordId: success.recordId,
				score,
			});
			setSuccess((current) =>
				current
					? {
							...current,
							nextAction: response.data.nextAction,
							nextSuggestion: response.data.nextSuggestion,
						}
					: current,
			);
			void refreshBootstrap();
			showToast("反馈已用于下一次推荐");
		} catch {
			showToast("反馈保存失败，请检查后端接口");
		}
	};

	const joinGroup = async (groupId: string) => {
		if (!requireAuth(() => joinGroup(groupId))) return;
		try {
			await joinGrowthGroup({ groupId });
			setGroups((items) =>
				items.map((group) => (group.id === groupId ? { ...group, joined: true } : group)),
			);
			showToast("已加入小组");
		} catch (error) {
			showToast(getApiError(error)?.message ?? "加入小组失败，请检查后端接口");
		}
	};

	const applyGroupReactions = (
		noteId: string,
		myReactions: NonNullable<TimelineItem["myReactions"]>,
		reactions: NonNullable<TimelineItem["reactions"]>,
	) => {
		const update = (items: TimelineItem[]) =>
			items.map((item) => (item.id === noteId ? { ...item, myReactions, reactions } : item));
		setTimeline(update);
		setGroupTimeline(update);
	};

	const reactToGroupNote = async (
		noteId: string,
		reactionType: GroupReactionType,
		nextActive: boolean,
	) => {
		if (!requireAuth(() => reactToGroupNote(noteId, reactionType, nextActive))) return;
		const currentItem =
			groupTimeline.find((item) => item.id === noteId) ??
			timeline.find((item) => item.id === noteId);
		const previousMyReactions = currentItem?.myReactions ?? { seen: false, support: false };
		const previousReactions = currentItem?.reactions ?? { seen: 0, support: 0 };
		const optimisticMyReactions = {
			...previousMyReactions,
			[reactionType]: nextActive,
		};
		const optimisticReactions = {
			...previousReactions,
			[reactionType]: Math.max(0, previousReactions[reactionType] + (nextActive ? 1 : -1)),
		};
		applyGroupReactions(noteId, optimisticMyReactions, optimisticReactions);
		try {
			const response = await createGroupReaction({ noteId, reactionType });
			applyGroupReactions(noteId, response.data.myReactions, response.data.reactions);
		} catch (error) {
			applyGroupReactions(noteId, previousMyReactions, previousReactions);
			showToast(getApiError(error)?.message ?? "回应失败，请检查后端接口");
		}
	};

	if (!authSession || authLoading || isAuthCallback) {
		return (
			<main className="phone-login-page" style={FOUNDATION_STYLE}>
				<div className="phone-login-layout">
					<div className="phone-login-content">
						<PhoneLogin
							onLogin={(session) => {
								setAuthSession(session);
								setAuthLoading(false);
								window.history.replaceState(null, "", "/");
							}}
						/>
					</div>
					{toast && <div className="gh-toast">{toast}</div>}
				</div>
			</main>
		);
	}

	return (
		<main className="guanghe-page" style={FOUNDATION_STYLE}>
			<div className="phone-shell" aria-label={`${PRODUCT_NAME} App`}>
				<div className="phone-viewport">
					{tab === "home" && (
						<HomeScreen
							onDraw={openDrawFlow}
							onGoPractice={openPractice}
							onKeywordConfirm={confirmTodayKeyword}
							onOpeningChange={setOpeningLine}
							onOpeningConfirm={confirmOpeningLine}
							onSummaryChange={setTodayKeyword}
							onViewRecords={() => {
								setFlow(null);
								setSelectedPractice(null);
								setTab("records");
							}}
							keywordConfirmed={keywordConfirmed}
							ongoing={ongoing}
							openingConfirmed={openingConfirmed}
							openingLine={openingLine}
							openingSaving={openingSaving}
							practiceMap={practiceMap}
							stateEncouragement={stateEncouragement}
							todayCompleted={todayCompleted}
							todayKeyword={todayKeyword}
							todayPractice={todayPractice}
						/>
					)}
					{tab === "practice" && (
						<PracticeScreen
							activeIds={ongoing.map((item) => item.practiceId)}
							isLoading={bootstrapLoading}
							loadError={practiceLoadError}
							onOpenPractice={openPractice}
							onToggleOngoing={(practiceId) => {
								void toggleOngoing(practiceId);
							}}
							practices={practices}
							subskills={subskills}
						/>
					)}
					{tab === "records" && (
						<RecordsScreen
							groupItems={groupTimeline}
							items={timeline}
							onCreateNote={() => runProtected(() => setFlow("note"))}
							onReact={reactToGroupNote}
						/>
					)}
					{tab === "mine" && (
						<MineScreen
							authSession={authSession}
							badges={badges}
							dimensionScores={dimensionScoreValues}
							groups={groups}
							onAuthLogin={() => void startPhoneLogin()}
							onAuthLogout={handleAuthLogout}
							onJoinGroup={(groupId) => runProtected(() => joinGroup(groupId))}
							profile={profile}
							relations={relations}
							report={report}
							timeline={timeline}
						/>
					)}
				</div>
				<BottomNav
					active={tab}
					onNote={() => runProtected(() => setFlow("note"))}
					onTab={openTab}
				/>

				{flow === "draw" && (
					<DrawFlow
						keyword={openingLine}
						onClose={() => setFlow(null)}
						onDraw={drawToday}
						onGo={(practice) => {
							setSelectedPractice(practice);
							setFlow("detail");
						}}
						todayPractice={todayPractice}
					/>
				)}
				{flow === "detail" && selectedPractice && (
					<PracticeDetail
						onClose={() => {
							setFlow(null);
							setSelectedPractice(null);
						}}
						onRecord={(practice) => {
							runProtected(() => {
								setFlow(null);
								setRecordFor(practice);
							});
						}}
						onToggleOngoing={(practiceId) => {
							void toggleOngoing(practiceId);
						}}
						onViewRecords={() => {
							setFlow(null);
							setSelectedPractice(null);
							setTab("records");
						}}
						evidenceRules={evidenceRules}
						isOngoing={ongoing.some((item) => item.practiceId === selectedPractice.id)}
						isCompleted={todayCompleted && todayPractice?.id === selectedPractice.id}
						practice={selectedPractice}
						subskills={subskills}
					/>
				)}
				{recordFor && (
					<RecordSheet
						onClose={() => setRecordFor(null)}
						onDone={completePractice}
						practice={recordFor}
					/>
				)}
				{flow === "note" && (
					<NoteSheet groups={groups} onClose={() => setFlow(null)} onPublish={publishNote} />
				)}
				{success && (
					<SuccessOverlay
						entry={success.entry}
						onClose={() => setSuccess(null)}
						onHome={() => {
							setSuccess(null);
							setTab("home");
						}}
						onShare={() => {
							runProtected(() => {
								setShareFor(success.entry);
								setSuccess(null);
							});
						}}
						onFeedback={submitFeedback}
						practice={success.practice}
					/>
				)}
				{shareFor && (
					<ShareSheet
						entry={shareFor}
						onClose={() => setShareFor(null)}
						onShared={addRelationAsset}
					/>
				)}
				{authLoading && (
					<div className="auth-redirect-mask">
						<div className="auth-redirect-card">
							<strong>正在打开登录</strong>
							<span>登录后会回到积极心理学小练习</span>
						</div>
					</div>
				)}
				{toast && <div className="gh-toast">{toast}</div>}
			</div>
		</main>
	);
};

const DimChip: React.FC<{ dim: DimensionId; quiet?: boolean }> = ({ dim, quiet }) => {
	const dimension = DIM[dim];
	const Icon = dimension.Icon;
	return (
		<span
			className={cn("dim-chip", quiet && "quiet")}
			style={
				{
					"--dim": dimension.color,
					"--dim-soft": dimension.soft,
					"--dim-deep": dimension.deep,
				} as React.CSSProperties
			}
		>
			<Icon size={13} strokeWidth={2.4} />
			{dimension.name}
		</span>
	);
};

const Avatar: React.FC<{ label: string; size?: number; src?: string }> = ({
	label,
	size = 44,
	src,
}) => {
	const [failed, setFailed] = useState(false);
	useEffect(() => {
		setFailed(false);
	}, [src]);

	return (
		<div className="avatar" style={{ height: size, width: size }}>
			{failed || !src ? (
				<span className="avatar-default" aria-label={`默认头像：${label}`} role="img">
					<User size={Math.max(18, Math.round(size * 0.52))} strokeWidth={2.4} />
				</span>
			) : (
				<img
					alt={src ? `${label}的头像` : `默认头像：${label}`}
					onError={() => setFailed(true)}
					src={src}
				/>
			)}
		</div>
	);
};

// 五维插画 / placeholder-art — abstract on-brand scenes that give every module imagery.
const DIM_MOTIF: Record<DimensionId, React.ReactNode> = {
	aware: (
		<>
			<circle cx="68" cy="30" r="14" fill="currentColor" opacity="0.9" />
			<g stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" opacity="0.55">
				<path d="M68 8v6" />
				<path d="M88 30h-6" />
				<path d="M83 15l-4 4" />
			</g>
			<path d="M0 64 Q26 46 50 60 T100 56 V84 H0 Z" fill="currentColor" opacity="0.16" />
			<path d="M0 74 Q30 60 56 72 T100 70 V84 H0 Z" fill="currentColor" opacity="0.34" />
			<path d="M24 40l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="currentColor" opacity="0.85" />
		</>
	),
	strength: (
		<>
			<circle cx="24" cy="24" r="9" fill="currentColor" opacity="0.85" />
			<path d="M0 84 L34 38 L56 70 L74 44 L100 84 Z" fill="currentColor" opacity="0.34" />
			<path d="M40 84 L74 44 L100 84 Z" fill="currentColor" opacity="0.18" />
			<path d="M74 44 v-16 l13 7 -13 6" fill="currentColor" opacity="0.9" />
		</>
	),
	relate: (
		<>
			<circle cx="38" cy="42" r="20" fill="currentColor" opacity="0.22" />
			<circle cx="64" cy="42" r="20" fill="currentColor" opacity="0.34" />
			<path
				d="M50 30c4-6 14-4 14 3 0 6-9 11-14 15-5-4-14-9-14-15 0-7 10-9 14-3z"
				fill="currentColor"
				opacity="0.9"
			/>
		</>
	),
	resil: (
		<>
			<path
				d="M6 78 H94"
				stroke="currentColor"
				strokeWidth="2.6"
				strokeLinecap="round"
				opacity="0.3"
			/>
			<path
				d="M50 78 V44"
				stroke="currentColor"
				strokeWidth="3.4"
				strokeLinecap="round"
				opacity="0.85"
			/>
			<path d="M50 52 C36 50 30 40 30 30 c12 0 20 8 20 22z" fill="currentColor" opacity="0.5" />
			<path d="M50 46 C64 44 72 33 72 22 c-12 0-22 9-22 24z" fill="currentColor" opacity="0.85" />
		</>
	),
	hope: (
		<>
			<path
				d="M8 70 Q40 64 60 40 T96 16"
				stroke="currentColor"
				strokeWidth="2.4"
				strokeDasharray="2 7"
				strokeLinecap="round"
				opacity="0.5"
				fill="none"
			/>
			<path d="M58 30 L92 14 L80 50 L70 38 Z" fill="currentColor" opacity="0.9" />
			<path d="M70 38 L80 50 L70 52 Z" fill="currentColor" opacity="0.55" />
			<path
				d="M20 26l1.6 4 4 1.6-4 1.6-1.6 4-1.6-4-4-1.6 4-1.6z"
				fill="currentColor"
				opacity="0.8"
			/>
		</>
	),
};

const DimScene: React.FC<{
	className?: string;
	dim: DimensionId;
	preserveAspectRatio?: React.SVGProps<SVGSVGElement>["preserveAspectRatio"];
}> = ({ className, dim, preserveAspectRatio }) => (
	<div aria-hidden="true" className={cn("dim-scene", className)} style={{ color: DIM[dim].color }}>
		<svg
			fill="none"
			preserveAspectRatio={preserveAspectRatio}
			viewBox="0 0 100 84"
			xmlns="http://www.w3.org/2000/svg"
		>
			{DIM_MOTIF[dim]}
		</svg>
	</div>
);

// Compact framed art used inside list rows — a soft tile with the scene + dimension hue.
const DimArt: React.FC<{ dim: DimensionId; size?: number }> = ({ dim, size = 56 }) => (
	<div
		className="dim-art"
		style={
			{
				"--art": DIM[dim].color,
				"--art-soft": DIM[dim].soft,
				height: size,
				width: size,
			} as React.CSSProperties
		}
	>
		<DimScene dim={dim} />
	</div>
);

export const HomeScreen: React.FC<{
	keywordConfirmed: boolean;
	ongoing: Ongoing[];
	openingConfirmed: boolean;
	openingLine: string;
	openingSaving: boolean;
	practiceMap: Record<string, Practice>;
	stateEncouragement: ApiStateEncouragement | null;
	todayCompleted: boolean;
	todayKeyword: string;
	todayPractice: Practice | null;
	onDraw: () => void | Promise<Practice>;
	onGoPractice: (id?: string) => void;
	onKeywordConfirm: () => void;
	onOpeningChange: (value: string) => void;
	onOpeningConfirm: () => void;
	onSummaryChange: (value: string) => void;
	onViewRecords: () => void;
}> = ({
	keywordConfirmed,
	ongoing,
	openingConfirmed,
	openingLine,
	openingSaving,
	onDraw,
	onGoPractice,
	onKeywordConfirm,
	onOpeningChange,
	onOpeningConfirm,
	onSummaryChange,
	onViewRecords,
	practiceMap,
	stateEncouragement,
	todayCompleted,
	todayKeyword,
	todayPractice,
}) => {
	const openingText = openingLine.trim();
	const lockedOpening = openingConfirmed && Boolean(openingText);
	return (
		<section className="app-screen home-screen">
			<header className="home-head">
				<div className="home-kicker">此刻，不必完成很多</div>
				<h1>今天，从哪里开始？</h1>
			</header>

			{lockedOpening ? (
				<div className="opening-note opening-note-locked" aria-label="今日状态">
					<label>当前状态</label>
					<div className="opening-locked-content">
						<span>今天写下</span>
						<p>{openingText || "今天直接开始，今日状态已确认"}</p>
						{stateEncouragement ? (
							<div
								className={cn(
									"state-encouragement",
									stateEncouragement.matchType === "safety" && "safety",
								)}
							>
								<Sparkles size={15} />
								<strong>{stateEncouragement.content}</strong>
							</div>
						) : null}
					</div>
				</div>
			) : (
				<form
					className="opening-note"
					aria-label="今日状态"
					onSubmit={(event) => {
						event.preventDefault();
						onOpeningConfirm();
					}}
				>
					<label htmlFor="opening-line">当前状态</label>
					<div className="opening-note-row">
						<input
							id="opening-line"
							onChange={(event) => onOpeningChange(event.target.value)}
							placeholder="用一句话标记现在的状态"
							value={openingLine}
						/>
						<button
							className="opening-confirm"
							disabled={!openingLine.trim() || openingSaving}
							type="submit"
							aria-label="确认今日状态"
						>
							{openingSaving ? <span className="opening-saving-dot" /> : <Check size={21} />}
						</button>
					</div>
				</form>
			)}

			<div className="today-random-section" aria-label="今日随机练习">
				<div className="home-section-label">今日随机练习</div>
				<TodayPracticeCard
					completed={todayCompleted}
					onDraw={onDraw}
					onGoPractice={onGoPractice}
					onViewRecords={onViewRecords}
					practice={todayPractice}
				/>
			</div>

			<ContinuousPracticeSection
				ongoing={ongoing}
				onGoPractice={onGoPractice}
				practiceMap={practiceMap}
			/>

			<KeywordCard
				confirmed={keywordConfirmed}
				onChange={onSummaryChange}
				onConfirm={onKeywordConfirm}
				value={todayKeyword}
			/>
		</section>
	);
};

const ContinuousPracticeSection: React.FC<{
	ongoing: Ongoing[];
	onGoPractice: (id?: string) => void;
	practiceMap: Record<string, Practice>;
}> = ({ ongoing, onGoPractice, practiceMap }) => {
	const ongoingItems = ongoing.filter((item) => practiceMap[item.practiceId]);

	return (
		<section className="section-block continuous-practice-section">
			<div className="section-title single">
				<div>
					<span>持续练习</span>
				</div>
				<button className="text-link" onClick={() => onGoPractice()} type="button">
					去添加
					<ArrowRight size={15} />
				</button>
			</div>
			{ongoingItems.length > 0 ? (
				<div className="continuous-practice-grid">
					{ongoingItems.map((item) => (
						<ContinuousPracticeCard
							item={item}
							key={item.id}
							onGoPractice={onGoPractice}
							practiceMap={practiceMap}
						/>
					))}
				</div>
			) : (
				<div className="continuous-practice-empty">
					<span className="continuous-empty-icon" aria-hidden="true">
						<BookOpen size={22} />
					</span>
					<div className="continuous-empty-copy">
						<strong>还没有持续练习</strong>
						<p>加入一项后，就能从这里接着做。</p>
					</div>
					<button onClick={() => onGoPractice()} type="button">
						去添加
					</button>
				</div>
			)}
		</section>
	);
};

const ContinuousPracticeCard: React.FC<{
	item?: Ongoing;
	practice?: Practice;
	practiceMap?: Record<string, Practice>;
	onGoPractice: (id: string) => void;
}> = ({ item, onGoPractice, practice: providedPractice, practiceMap }) => {
	const practice = providedPractice ?? (item && practiceMap?.[item.practiceId]);
	if (!practice) return null;
	const dimension = DIM[practice.dim];
	const weekDone = item?.week.reduce((sum, value) => sum + value, 0) ?? 0;
	const currentDay = item?.today ?? (practice.dim === "strength" ? 3 : 1);
	const progress = item
		? Math.max(8, Math.min(100, Math.round((weekDone / 7) * 100)))
		: practice.dim === "relate"
			? 24
			: 52;
	const subtitle = item
		? `${item.days > 0 ? `已练习 ${item.days} 天` : "刚开始练习"}`
		: practice.dim === "relate"
			? "21 天计划"
			: "7 天发现";
	const actionLabel = item?.checked ? "查看" : "去练习";
	return (
		<article
			className={cn("continuous-practice-card", `is-${practice.dim}`)}
			style={
				{
					"--dim": dimension.color,
					"--dim-soft": dimension.soft,
					"--progress": `${progress}%`,
				} as React.CSSProperties
			}
		>
			<DimArt dim={practice.dim} size={40} />
			<h3>{practice.title}</h3>
			<p>{subtitle}</p>
			<div className="continuous-progress" aria-hidden="true">
				<span />
			</div>
			<div className="continuous-card-foot">
				<span>第 {currentDay} 天</span>
				<button onClick={() => onGoPractice(practice.id)} type="button">
					{actionLabel}
					<ArrowRight size={13} />
				</button>
			</div>
		</article>
	);
};

const KeywordCard: React.FC<Omit<React.ComponentProps<typeof SharedKeywordCard>, "words">> = (
	props,
) => <SharedKeywordCard {...props} words={SUMMARY_WORDS} />;

const TodayPracticeCard: React.FC<{
	completed: boolean;
	practice: Practice | null;
	onDraw: () => void | Promise<Practice>;
	onGoPractice: (id: string) => void;
	onViewRecords: () => void;
}> = ({ completed, onDraw, onGoPractice, onViewRecords, practice }) => {
	if (!practice) {
		return (
			<button
				aria-label="翻开今日练习卡"
				className="today-card draw-entry-card"
				onClick={() => void onDraw()}
				type="button"
			>
				<div className="draw-entry-copy">
					<div className="draw-entry-card-meta">
						<div className="today-card-kicker">
							<Sparkles size={15} />
							今日行动
						</div>
						<span className="draw-entry-duration">约 3 分钟</span>
					</div>
					<h2>翻开今天的练习卡</h2>
					<p className="today-empty-hint">不必一次改变很多，先找到此刻能做的一件小事。</p>
					<span className="draw-entry-action">
						看看今天的练习
						<ArrowRight size={16} />
					</span>
				</div>
				<div className="draw-entry-visual" aria-hidden="true">
					<img alt="" className="draw-entry-asset" src={PRACTICE_ART_SRC} />
				</div>
			</button>
		);
	}
	const dimension = DIM[practice.dim];
	return (
		<section
			className={cn("today-card", completed && "completed")}
			style={
				{
					"--active-dim": dimension.color,
					"--active-dim-soft": dimension.soft,
					"--active-dim-deep": dimension.deep,
				} as React.CSSProperties
			}
		>
			<div className="today-art" aria-hidden="true">
				<img alt="" src={PRACTICE_ART_SRC} />
			</div>
			<div className="today-status">
				<DimChip dim={practice.dim} />
				<span>{completed ? "已完成" : "待练习"}</span>
			</div>
			<h2>{practice.title}</h2>
			<p>{practice.intro}</p>
			<div className="today-steps">
				{practice.steps.map((step, index) => (
					<div className="today-step" key={step}>
						<span>{index + 1}</span>
						<p>{step}</p>
					</div>
				))}
			</div>
			<button
				className="primary-action"
				onClick={() => (completed ? onViewRecords() : onGoPractice(practice.id))}
				type="button"
			>
				{completed ? "查看完成记录" : "去练习"}
				<ArrowRight size={16} />
			</button>
		</section>
	);
};

type TimelineView = "personal" | "group";

export const RecordsScreen: React.FC<{
	groupItems: TimelineItem[];
	items: TimelineItem[];
	onCreateNote: () => void;
	onReact: (noteId: string, reactionType: GroupReactionType, nextActive: boolean) => void;
}> = ({ groupItems, items, onCreateNote, onReact }) => {
	const [view, setView] = useState<TimelineView>("personal");
	const practiceCount = items.filter((item) => item.kind === "practice").length;
	const mediaCount = items.filter((item) => item.image || item.voice > 0).length;
	const visibleItems = view === "group" ? groupItems : items;
	const firstItem = visibleItems[0];
	const clueDimensions = Array.from(
		new Set(visibleItems.map((item) => item.dim).filter((dim): dim is DimensionId => Boolean(dim))),
	).slice(0, 3);
	let insight: { headline: string; note: string } | null = null;
	if (firstItem) {
		if (view === "group") {
			insight = {
				headline: `${firstItem.authorName ?? "一位组员"}留下了一条真实记录。`,
				note: `最近的小组时间轴里共有 ${visibleItems.length} 条记录。`,
			};
		} else if (firstItem.kind === "practice") {
			insight = {
				headline: `你把「${firstItem.title ?? "一次练习"}」变成了可以回看的证据。`,
				note: `最近共留下 ${visibleItems.length} 条练习与随心记录。`,
			};
		} else {
			insight = {
				headline: `你为“${firstItem.text.slice(0, 18)}${firstItem.text.length > 18 ? "…" : ""}”留下了真实记录。`,
				note: "随心记也会成为理解自己的线索。",
			};
		}
	}
	const emptyText =
		view === "group"
			? "加入小组并发布随心记后，这里会出现小组成员的真实记录。"
			: "完成练习或写下随心记后，时间轴会按时间保存你的真实变化。";
	return (
		<section className={cn("app-screen records-screen", !items.length && "records-empty")}>
			<header className="records-head">
				<div className="records-head-row">
					<div className="records-title">
						<h1>我的成长证据</h1>
						<span>这里收的是你看见的变化，不是完成天数。</span>
					</div>
					<button
						aria-label="写一条随心记"
						className="records-compose"
						onClick={onCreateNote}
						type="button"
					>
						<PenLine size={18} />
					</button>
				</div>
				<div className="records-summary">
					<span>
						<strong>{items.length}</strong>条个人记录
					</span>
					<span>
						<strong>{practiceCount}</strong>次练习
					</span>
					<span>
						<strong>{mediaCount}</strong>条带媒体
					</span>
				</div>
				<div className="records-filter records-view-tabs" role="tablist" aria-label="记录视图">
					{[
						{ id: "personal" as const, label: "个人", count: items.length },
						{ id: "group" as const, label: "小组", count: groupItems.length },
					].map((item) => (
						<button
							aria-selected={view === item.id}
							className={cn(view === item.id && "active")}
							key={item.id}
							onClick={() => setView(item.id)}
							role="tab"
							type="button"
						>
							{item.label}
							<span>{item.count}</span>
						</button>
					))}
				</div>
			</header>
			{insight && (
				<section className="records-insight">
					<div className="records-insight-meta">
						<span>
							<Sparkles size={15} />
							最近看见的变化
						</span>
						<strong>{visibleItems.length} 条证据</strong>
					</div>
					<h2>{insight.headline}</h2>
					<p>{insight.note}</p>
					<div className="records-insight-foot">
						<span>成长不是直线，变化仍然在发生</span>
						<span className="records-insight-mark">✦</span>
					</div>
				</section>
			)}
			{clueDimensions.length > 0 && (
				<div className="records-clues">
					<span>能力线索</span>
					{clueDimensions.map((dim) => (
						<DimChip dim={dim} key={dim} quiet />
					))}
				</div>
			)}
			<div className={cn("timeline-list", !visibleItems.length && "is-empty")}>
				{visibleItems.length ? (
					visibleItems.map((item) => (
						<TimelineCard item={item} key={`${view}-${item.id}`} onReact={onReact} />
					))
				) : (
					<EmptyState
						actionLabel="写一条随心记"
						className="timeline-empty"
						icon={PenLine}
						onAction={onCreateNote}
						text={emptyText}
						title={`还没有${view === "group" ? "小组" : "个人"}记录`}
						tone="hope"
					/>
				)}
			</div>
		</section>
	);
};

const TimelineCard: React.FC<{
	item: TimelineItem;
	onReact?: (noteId: string, reactionType: GroupReactionType, nextActive: boolean) => void;
}> = ({ item, onReact }) => {
	const dimension = item.dim ? DIM[item.dim] : null;
	const isGroupNote = item.kind === "note" && item.visibility === "group";
	const isFriendShared = item.visibility === "friend" || item.friendName;
	const seenReactionCount = item.reactions?.seen ?? 0;
	const supportReactionCount = item.reactions?.support ?? 0;
	const seenReactionActive = item.myReactions?.seen ?? false;
	const supportReactionActive = item.myReactions?.support ?? false;
	const [animatingReaction, setAnimatingReaction] = useState<{
		direction: GroupReactionDirection;
		type: GroupReactionType;
	} | null>(null);
	const reactionAnimationTimer = useRef<number | null>(null);
	useEffect(
		() => () => {
			if (reactionAnimationTimer.current) window.clearTimeout(reactionAnimationTimer.current);
		},
		[],
	);
	const triggerReaction = (reactionType: GroupReactionType) => {
		if (!onReact) return;
		const isCurrentlyActive = reactionType === "seen" ? seenReactionActive : supportReactionActive;
		const nextActive = !isCurrentlyActive;
		if (reactionAnimationTimer.current) window.clearTimeout(reactionAnimationTimer.current);
		setAnimatingReaction({
			direction: nextActive ? "increment" : "decrement",
			type: reactionType,
		});
		reactionAnimationTimer.current = window.setTimeout(() => {
			setAnimatingReaction(null);
			reactionAnimationTimer.current = null;
		}, 360);
		onReact(item.id, reactionType, nextActive);
	};
	const getReactionClassName = (reactionType: GroupReactionType, active: boolean) =>
		cn(
			active && "is-active",
			animatingReaction?.type === reactionType && "is-animating",
			animatingReaction?.type === reactionType &&
				animatingReaction.direction === "increment" &&
				"is-incrementing",
			animatingReaction?.type === reactionType &&
				animatingReaction.direction === "decrement" &&
				"is-decrementing",
		);
	return (
		<article
			className="timeline-card"
			style={{ "--pin": dimension ? dimension.color : "" } as React.CSSProperties}
		>
			<div className="timeline-pin" />
			<div className="timeline-body">
				<div className="timeline-meta">
					<span>{item.time}</span>
					<div className="timeline-tags">
						{dimension ? (
							<DimChip dim={dimension.id} quiet />
						) : (
							<span className="note-tag">
								{isGroupNote
									? `小组 · ${item.targetGroupName ?? "练习小组"}`
									: isFriendShared
										? `好友 · ${item.friendName ?? "朋友"}`
										: "随心记 · 仅自己"}
							</span>
						)}
						{dimension && item.friendName && (
							<span className="share-tag">已分享给 {item.friendName}</span>
						)}
					</div>
				</div>
				{item.title ? (
					<h2>{item.title}</h2>
				) : isGroupNote ? (
					<h2>{item.authorName ?? "组员"}的随心记</h2>
				) : null}
				<p>{item.text}</p>
				{item.image && <TimelineImage source={item.image} />}
				{item.voice > 0 && <VoiceBar audioSrc={item.voiceAudio} seconds={item.voice} />}
				{isGroupNote && onReact && (
					<div className="group-reactions">
						<button
							aria-label={`${seenReactionActive ? "取消我看见了" : "我看见了"} ${seenReactionCount}`}
							aria-pressed={seenReactionActive}
							className={getReactionClassName("seen", seenReactionActive)}
							onClick={() => triggerReaction("seen")}
							type="button"
						>
							<span aria-hidden="true" className="reaction-icon">
								👀
							</span>
							<span className="reaction-count">{seenReactionCount}</span>
						</button>
						<button
							aria-label={`${supportReactionActive ? "取消支持你" : "支持你"} ${supportReactionCount}`}
							aria-pressed={supportReactionActive}
							className={getReactionClassName("support", supportReactionActive)}
							onClick={() => triggerReaction("support")}
							type="button"
						>
							<span aria-hidden="true" className="reaction-icon">
								👍
							</span>
							<span className="reaction-count">{supportReactionCount}</span>
						</button>
					</div>
				)}
			</div>
		</article>
	);
};

const VoiceBar: React.FC<{ audioSrc?: string | null; seconds: number }> = ({
	audioSrc,
	seconds,
}) => {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [playing, setPlaying] = useState(false);
	const resolvedAudioSrc = useResolvedMedia(audioSrc);
	const canPlay = Boolean(resolvedAudioSrc);

	const togglePlay = () => {
		const audio = audioRef.current;
		if (!audio || !canPlay) return;
		if (playing) {
			audio.pause();
			setPlaying(false);
			return;
		}
		void audio
			.play()
			.then(() => setPlaying(true))
			.catch(() => setPlaying(false));
	};

	return (
		<button
			aria-label={canPlay ? (playing ? "暂停语音" : "播放语音") : "暂无可播放语音"}
			className={cn("voice-bar", playing && "is-playing")}
			disabled={!canPlay}
			onClick={togglePlay}
			type="button"
		>
			<span>
				{playing ? <Pause fill="currentColor" size={12} /> : <Play fill="currentColor" size={12} />}
			</span>
			<div className="voice-waves">
				{Array.from({ length: 18 }).map((_, index) => (
					<i key={index} style={{ height: 5 + Math.abs(Math.sin(index * 1.3)) * 17 }} />
				))}
			</div>
			<em>{seconds}"</em>
			{resolvedAudioSrc && (
				<audio
					onEnded={() => setPlaying(false)}
					onPause={() => setPlaying(false)}
					ref={audioRef}
					src={resolvedAudioSrc}
				/>
			)}
		</button>
	);
};

const DimensionPentagon: React.FC<{ scores: Record<DimensionId, number> }> = ({ scores }) => {
	const realTotal = DIMENSIONS.reduce((sum, dimension) => sum + scores[dimension.id], 0);
	const metrics = GROWTH_LEDGER.map((row) => ({
		...row,
		color: DIM[row.dim].color,
		score: scores[row.dim],
		deep: DIM[row.dim].deep,
		short: DIM[row.dim].short,
	}));
	const maxScore = Math.max(1, Math.min(12, Math.max(...metrics.map((item) => item.score))));
	const center = 112;
	const radius = 68;
	const labelRadius = 84;
	const labelBoxWidth = 44;
	const labelBoxHeight = 22;
	const angleOf = (index: number) => -Math.PI / 2 + (index * Math.PI * 2) / metrics.length;
	const point = (index: number, pointRadius: number) => {
		const angle = angleOf(index);
		return {
			x: center + Math.cos(angle) * pointRadius,
			y: center + Math.sin(angle) * pointRadius,
		};
	};
	const polygonPoints = (scale: number) =>
		metrics
			.map((_, index) => {
				const current = point(index, radius * scale);
				return `${current.x},${current.y}`;
			})
			.join(" ");
	const valuePoints = metrics
		.map((item, index) => {
			const scale = Math.min(1, item.score / maxScore);
			const current = point(index, radius * scale);
			return `${current.x},${current.y}`;
		})
		.join(" ");

	return (
		<article className={cn("dimension-pentagon-card", realTotal === 0 && "is-empty")}>
			<div className="pentagon-visual" aria-label="五维行动雷达图" role="img">
				<svg viewBox="0 0 220 220">
					{[0.2, 0.4, 0.6, 0.8, 1].map((scale) => (
						<polygon className="pentagon-grid" key={scale} points={polygonPoints(scale)} />
					))}
					{metrics.map((item, index) => {
						const outer = point(index, radius);
						return (
							<line
								className="pentagon-axis"
								key={item.dim}
								x1={center}
								x2={outer.x}
								y1={center}
								y2={outer.y}
							/>
						);
					})}
					<polygon className="pentagon-value" points={valuePoints} />
					{metrics.map((item, index) => {
						const outer = point(index, radius);
						const label = point(index, labelRadius);
						const abilityScale = Math.min(1, item.score / maxScore);
						const abilityPoint = point(index, radius * abilityScale);
						return (
							<g key={item.dim}>
								<circle className="pentagon-outer-dot" cx={outer.x} cy={outer.y} r="2.9" />
								<rect
									className="pentagon-label-bg"
									height={labelBoxHeight}
									rx="11"
									width={labelBoxWidth}
									x={label.x - labelBoxWidth / 2}
									y={label.y - labelBoxHeight / 2}
								/>
								<text
									className="pentagon-label"
									dominantBaseline="middle"
									textAnchor="middle"
									x={label.x}
									y={label.y}
								>
									{item.short}
								</text>
								{item.score > 0 && (
									<circle
										className="pentagon-value-point"
										cx={abilityPoint.x}
										cy={abilityPoint.y}
										r="4.2"
									/>
								)}
							</g>
						);
					})}
				</svg>
			</div>
		</article>
	);
};

export const MineScreen: React.FC<{
	preview?: boolean;
	authSession: UserSession | null;
	badges: Badge[];
	dimensionScores: Record<DimensionId, number>;
	groups: GrowthGroup[];
	profile: ProfileStats;
	relations: RelationAsset[];
	report: GrowthReport;
	timeline: TimelineItem[];
	onAuthLogin: () => void;
	onAuthLogout: () => void;
	onJoinGroup: (groupId: string) => void;
}> = ({
	authSession,
	badges,
	dimensionScores,
	groups,
	onAuthLogin,
	onAuthLogout,
	onJoinGroup,
	preview = false,
	profile,
	relations,
	report,
	timeline,
}) => {
	const hasReportData = report.ranges.some((range) => range.total > 0);
	const earnedBadges = badges.filter((badge) => badge.got);

	return (
		<section className="app-screen mine-screen">
			<header className="mine-page-head">
				<div>
					<span>你的练习记录</span>
					<h1>我的</h1>
				</div>
				<img alt="" aria-hidden="true" className="mine-page-art" src={BRAND_ART_SRC} />
			</header>

			<section className="personal-overview-card" id="badges">
				<ProfileSummary
					name={authSession?.phone?.trim() || profile.name}
					status={profile.joined}
					avatar={<Avatar label={profile.avTxt} size={58} src={profile.avatar} />}
					actionLabel={preview ? "预览" : authSession ? "退出" : "登录"}
					actionDisabled={preview}
					onAction={authSession ? onAuthLogout : onAuthLogin}
				/>
				<div className="overview-divider" />
				<div className="badge-summary-row">
					<div>
						<div className="overview-title-row">
							<strong>我的徽章</strong>
							<span>
								{earnedBadges.length} / {badges.length}
							</span>
						</div>
						<p>
							{earnedBadges.length
								? `已经解锁 ${earnedBadges.length} 个成长印记。`
								: "完成几次真实行动后，会在这里留下成长印记。"}
						</p>
						<a href="#badges">查看全部徽章 ›</a>
					</div>
					<div className="badge-mini-stack" aria-hidden="true">
						{badges.slice(0, 3).map((badge, index) => {
							const Icon = badge.got ? badge.Icon : LockKeyhole;
							return (
								<span
									className={cn("badge-mini", badge.got && "got")}
									key={badge.id}
									style={
										{
											"--badge": DIM[badge.dim].color,
											"--badge-deep": DIM[badge.dim].deep,
											left: `${index * 18}px`,
											top: index === 1 ? "8px" : "0px",
										} as React.CSSProperties
									}
								>
									<Icon size={16} />
								</span>
							);
						})}
					</div>
				</div>
				<div className="overview-divider" />
				<DimensionEvidence dimensionScores={dimensionScores} timeline={timeline} />
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>成长档案</span>
						<h2>五维行动雷达</h2>
					</div>
				</div>
				<DimensionPentagon scores={dimensionScores} />
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>周期报告</span>
						<h2>最近变化</h2>
					</div>
				</div>
				<article className={cn("report-card", !hasReportData && "is-empty")}>
					<DimChip dim={hasReportData ? report.focusDimension : "aware"} />
					<h3>{hasReportData ? report.headline : "报告还在等待证据"}</h3>
					<p>
						{hasReportData
							? report.nextDecision
							: "先留下几次练习记录，系统再帮你看最近哪个方向开始变化。"}
					</p>
					<div className="report-ranges">
						{report.ranges.map((range) => (
							<span key={range.days}>
								<b>{range.total.toFixed(1)}</b>
								最近 {range.days} 天
							</span>
						))}
					</div>
				</article>
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>我的关系</span>
						<h2>分享后的联结记录</h2>
					</div>
				</div>
				<div className="relation-list">
					{relations.length ? (
						relations.map((relation) => (
							<article className="relation-card" key={relation.id}>
								<div className="relation-avatar" aria-hidden="true">
									<User size={21} strokeWidth={2.2} />
									<span>
										<HandHeart size={12} strokeWidth={2.4} />
									</span>
								</div>
								<div className="relation-copy">
									<em>{relation.tag}</em>
									<strong>{relation.name}</strong>
									<p>来自「{relation.from}」</p>
								</div>
								<div className="relation-mark" aria-hidden="true">
									<HandHeart size={17} strokeWidth={2.3} />
								</div>
							</article>
						))
					) : (
						<EmptyState
							icon={HandHeart}
							text="完成可分享练习后，可以把重要联结保存到这里。"
							title="还没有关系记录"
							tone="relate"
						/>
					)}
				</div>
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>我的小组</span>
						<h2>一起成长的人</h2>
					</div>
				</div>
				<div className="group-list">
					{groups.length ? (
						groups.map((group) => (
							<article
								className="group-card"
								key={group.id}
								style={
									{
										"--active-dim": DIM[group.dim].color,
										"--active-dim-soft": DIM[group.dim].soft,
									} as React.CSSProperties
								}
							>
								<div className="group-art" aria-hidden="true">
									<DimScene dim={group.dim} preserveAspectRatio="xMidYMid slice" />
								</div>
								<div className="group-body">
									<DimChip dim={group.dim} quiet />
									<strong>{group.name}</strong>
									<p>{group.description}</p>
									<div className="group-meta-row">
										<span className="group-session">
											{group.sessionLabel} · {group.sessionTitle} · {group.memberCount} 人
										</span>
										<button
											className={cn("ghost-action compact group-action", group.joined && "checked")}
											disabled={group.joined}
											onClick={() => onJoinGroup(group.id)}
											type="button"
										>
											{group.joined ? "已加入" : "加入"}
										</button>
									</div>
								</div>
							</article>
						))
					) : (
						<EmptyState
							icon={UsersRound}
							text="小组练习开放后，会在这里显示你加入的人和练习主题。"
							title="还没有加入小组"
							tone="relate"
						/>
					)}
				</div>
			</section>
		</section>
	);
};

export const DrawFlow: React.FC<{
	keyword: string;
	todayPractice: Practice | null;
	onClose: () => void;
	onDraw: () => Practice | Promise<Practice>;
	onGo: (practice: Practice) => void;
}> = ({ keyword, onClose, onDraw, onGo, todayPractice }) => {
	const [stage, setStage] = useState<"shuffle" | "lock" | "result" | "error">(
		todayPractice ? "result" : "shuffle",
	);
	const [attempt, setAttempt] = useState(0);
	const [errorMessage, setErrorMessage] = useState("");
	const [result, setResult] = useState<Practice | null>(todayPractice);
	const onDrawRef = useRef(onDraw);

	useEffect(() => {
		onDrawRef.current = onDraw;
	}, [onDraw]);

	useEffect(() => {
		if (!todayPractice) return;
		setErrorMessage("");
		setResult(todayPractice);
		setStage("result");
	}, [todayPractice]);

	useEffect(() => {
		if (todayPractice || result) return undefined;

		let cancelled = false;
		const lockTimer = window.setTimeout(() => setStage("lock"), 1900);
		const revealTimer = window.setTimeout(() => {
			void Promise.resolve(onDrawRef.current())
				.then((next) => {
					if (cancelled) return;
					setResult(next);
					setStage("result");
				})
				.catch((error: unknown) => {
					if (cancelled) return;
					setErrorMessage(getApiError(error, DRAW_ERROR_MESSAGE)?.message ?? DRAW_ERROR_MESSAGE);
					setStage("error");
				});
		}, 2850);

		return () => {
			cancelled = true;
			window.clearTimeout(lockTimer);
			window.clearTimeout(revealTimer);
		};
	}, [attempt, result, todayPractice]);

	const isLocking = stage === "lock";
	const retryDraw = () => {
		setErrorMessage("");
		setStage("shuffle");
		setAttempt((value) => value + 1);
	};

	return (
		<div className={cn("flow-screen draw-flow", `draw-${stage}`)}>
			<button aria-label="返回首页" className="draw-back-button" onClick={onClose} type="button">
				<ArrowLeft size={22} />
			</button>
			<div className="draw-flow-body">
				{stage !== "result" && stage !== "error" && (
					<>
						<div className="dark-heading">
							<span>DAILY DRAW</span>
							<h1>今日 PERMA 训练</h1>
							<p>{isLocking ? "正在为你锁定今日练习" : "五个方向正在随机轮转"}</p>
						</div>
						<div className="draw-stage">
							<div className="card-fan">
								{["P", "E", "A", "M", "R"].map((letter, index) => (
									<div className={`draw-tile tile-${index}`} key={letter}>
										{letter}
									</div>
								))}
							</div>
							<div className="question-card">
								<strong>?</strong>
								<span>PERMA</span>
							</div>
						</div>
						<div className="draw-footer draw-loading-footer" aria-live="polite">
							<div className="draw-status">
								<p>{isLocking ? "已抽中，准备揭晓" : "正在随机抽取今日训练..."}</p>
								<span>
									{isLocking
										? "结果已锁定 · 即将打开"
										: keyword.trim()
											? `带着「${keyword.trim()}」开始`
											: "纯随机 · 不可预测"}
								</span>
							</div>
							<div className="draw-progress" aria-hidden="true">
								<i />
								<i />
								<i />
							</div>
						</div>
					</>
				)}
				{stage === "error" && (
					<>
						<div className="dark-heading">
							<span>DAILY DRAW</span>
							<h1>暂时抽不到卡</h1>
							<p>{isDevBuild ? "后端接口没有成功返回" : "今天的练习服务暂时不可用"}</p>
						</div>
						<div className="draw-error-stage">
							<div className="draw-error-card">
								<span>{isDevBuild ? "接口异常" : "抽卡失败"}</span>
								<p>{errorMessage || (isDevBuild ? DRAW_DEV_HELP_TEXT : DRAW_ERROR_MESSAGE)}</p>
							</div>
						</div>
						<div className="draw-footer result-footer">
							{isDevBuild && <small>本地开发需要同时启动前端和 services/api</small>}
							<div className="result-actions">
								<button className="dark-secondary-action" onClick={onClose} type="button">
									回首页
								</button>
								<button className="dark-action" onClick={retryDraw} type="button">
									再试一次
									<ArrowRight size={16} />
								</button>
							</div>
						</div>
					</>
				)}
				{stage === "result" && result && (
					<>
						<div className="dark-heading">
							<span>DAILY DRAW</span>
							<h1>抽取完成</h1>
							<p>今天的练习已经为你落定</p>
						</div>
						<div className="draw-result-stage">
							<div
								className="draw-result-card"
								style={
									{
										"--active-dim": DIM[result.dim].color,
										"--active-dim-soft": DIM[result.dim].soft,
										"--active-dim-deep": DIM[result.dim].deep,
									} as React.CSSProperties
								}
							>
								<DimChip dim={result.dim} />
								<h1>{result.title}</h1>
								<span>{result.sub}</span>
								<p>{result.intro}</p>
							</div>
						</div>
						<div className="draw-footer result-footer">
							<div className="reveal-target">
								<span>今日目标</span>
								<p>{result.steps[0]}</p>
							</div>
							<small>已保存为今日练习 · 可回首页查看</small>
							<div className="result-actions">
								<button className="dark-secondary-action" onClick={onClose} type="button">
									回首页
								</button>
								<button className="dark-action" onClick={() => onGo(result)} type="button">
									开始练习
									<ArrowRight size={16} />
								</button>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export const PracticeDetail: React.FC<{
	evidenceRules: ApiEvidenceRule[];
	isOngoing: boolean;
	isCompleted: boolean;
	onClose: () => void;
	onRecord: (practice: Practice) => void;
	onToggleOngoing: (practiceId: string) => void;
	onViewRecords: () => void;
	practice: Practice;
	subskills: ApiSubskill[];
}> = ({
	isOngoing,
	isCompleted,
	onClose,
	onRecord,
	onToggleOngoing,
	onViewRecords,
	practice,
	subskills,
}) => {
	const dimension = DIM[practice.dim];
	const impactScores = practice.dimensionScores.length
		? practice.dimensionScores
		: [{ dim: practice.dim, isPrimary: true, source: "fallback", weight: 1 }];
	const primaryImpact = impactScores[0];
	const secondaryImpacts = impactScores
		.filter((item) => item.dim !== primaryImpact.dim)
		.slice(0, 2)
		.map((item) => DIM[item.dim].name);
	const dimensionSubskills = subskills
		.filter((item) => item.dimensionId === practice.dim)
		.slice(0, 4);
	return (
		<div className="flow-screen detail-flow">
			<div className="flow-head">
				<button className="icon-button" onClick={onClose} type="button">
					<ArrowLeft size={18} />
				</button>
				<strong>{dimension.name}</strong>
				<button className="icon-button" type="button" aria-label="收藏练习">
					<Bookmark size={17} />
				</button>
			</div>
			<div className="detail-scroll">
				<section
					className="detail-hero"
					style={
						{
							"--active-dim": dimension.color,
							"--active-dim-soft": dimension.soft,
							"--active-dim-deep": dimension.deep,
						} as React.CSSProperties
					}
				>
					<span className="detail-hero-chip">
						<Sparkles size={14} />
						{dimension.name} · {practice.sub}
					</span>
					<h1>{practice.title}</h1>
					<p>{practice.intro}</p>
					<div className="detail-hero-meta">
						{practice.sub} · {practice.steps.length} 个步骤
					</div>
				</section>
				{isCompleted && (
					<section className="detail-completed-card">
						<div className="completed-mark">
							<Check size={18} />
						</div>
						<div>
							<strong>今天这一步已完成</strong>
							<p>这条练习已经进入你的个人时间轴。可以回看记录，不需要重复提交。</p>
						</div>
					</section>
				)}
				{PRODUCT_FEATURES.videoPlayback && (
					<>
						<section className="detail-section-label">课程 · 讲解</section>
						<section className="course-card">
							<div className="course-cover" aria-hidden="true">
								<Play className="course-play" size={24} />
								<span>2:08</span>
							</div>
							<div className="course-body">
								<span>课程入口</span>
								<strong>{practice.course.title}</strong>
								<p>{practice.course.len} · 已看 0%</p>
							</div>
						</section>
					</>
				)}
				<section className="detail-section-label">评测 · 量表</section>
				<section className="assessment-card compact-assessment-card">
					<div className="assessment-card-head">
						<div className="support-icon">
							<ClipboardList size={17} />
						</div>
						<div>
							<strong>{practice.assess?.title ?? `${dimension.name}自查`}</strong>
							<p>
								{(practice.assess?.q ?? dimensionSubskills.length) || 3} 题 · 约 2 分钟 · 评测入口
							</p>
						</div>
					</div>
					<div className="assessment-scale" aria-hidden="true">
						{[1, 2, 3, 4, 5].map((value) => (
							<span key={value} className={value <= 3 ? "active" : ""}>
								<i />
								{value}
							</span>
						))}
					</div>
					<button className="assessment-start" type="button">
						开始评测
						<ArrowRight size={15} />
					</button>
				</section>
				<section className="steps-card action-steps-card">
					<div className="steps-card-head">
						<h2>练习动作</h2>
						<span>按这 3 步完成</span>
					</div>
					{practice.steps.map((step, index) => (
						<div className="step-row" key={`${practice.id}-${index}`}>
							<span>{index + 1}</span>
							<p>{step}</p>
						</div>
					))}
					<div className="record-hint">
						<span>记录时可以写</span>
						<p>{practice.recordPrompt}</p>
					</div>
				</section>
				<section className="impact-card">
					<span>练习影响</span>
					<strong>主要训练：{DIM[primaryImpact.dim].name}</strong>
					<p>
						{secondaryImpacts.length
							? `完成后也会帮助：${secondaryImpacts.join("、")}。`
							: "完成后会进入你的五维成长档案，让这一维度更饱满。"}
					</p>
				</section>
			</div>
			<div className="detail-actions">
				{isCompleted ? (
					<>
						<button className="ghost-action" onClick={onClose} type="button">
							回首页
						</button>
						<button className="primary-action" onClick={onViewRecords} type="button">
							<BookOpen size={17} />
							查看记录
						</button>
					</>
				) : (
					<>
						<button
							className={cn("ghost-action", isOngoing && "checked")}
							onClick={() => onToggleOngoing(practice.id)}
							type="button"
						>
							{isOngoing ? "已加入持续练习" : "加入持续练习"}
						</button>
						<button className="primary-action" onClick={() => onRecord(practice)} type="button">
							开始这个练习
						</button>
					</>
				)}
			</div>
		</div>
	);
};

const useSheetDrag = (onClose: () => void) => {
	const onCloseRef = useRef(onClose);
	const closeTimerRef = useRef<number | null>(null);
	const dragRef = useRef({
		active: false,
		lastTime: 0,
		lastY: 0,
		offset: 0,
		source: null as "pointer" | "touch" | null,
		startY: 0,
		velocity: 0,
	});
	const [dragY, setDragY] = useState(0);
	const [phase, setPhase] = useState<"idle" | "dragging" | "closing">("idle");

	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(
		() => () => {
			if (closeTimerRef.current !== null) {
				window.clearTimeout(closeTimerRef.current);
			}
		},
		[],
	);

	const closeWithMotion = useCallback(() => {
		if (closeTimerRef.current !== null) return;
		dragRef.current.active = false;
		setPhase("closing");
		setDragY(window.innerHeight);
		closeTimerRef.current = window.setTimeout(() => onCloseRef.current(), 180);
	}, []);

	const settleDrag = useCallback(() => {
		if (!dragRef.current.active) return;
		const { offset, velocity } = dragRef.current;
		dragRef.current.active = false;
		dragRef.current.source = null;
		if (offset > 96 || (offset > 42 && velocity > 0.55)) {
			closeWithMotion();
			return;
		}
		dragRef.current.offset = 0;
		setPhase("idle");
		setDragY(0);
	}, [closeWithMotion]);

	const startDrag = useCallback(
		(clientY: number, target: EventTarget | null, source: "pointer" | "touch") => {
			if (target instanceof Element && target.closest("button, input, textarea, select, a"))
				return false;
			const now = performance.now();
			dragRef.current = {
				active: true,
				lastTime: now,
				lastY: clientY,
				offset: 0,
				source,
				startY: clientY,
				velocity: 0,
			};
			setPhase("dragging");
			return true;
		},
		[],
	);

	const updateDrag = useCallback((clientY: number, source: "pointer" | "touch") => {
		if (!dragRef.current.active || dragRef.current.source !== source) return false;
		const now = performance.now();
		const rawOffset = clientY - dragRef.current.startY;
		const nextOffset = rawOffset >= 0 ? rawOffset : rawOffset * 0.18;
		const deltaTime = Math.max(1, now - dragRef.current.lastTime);
		dragRef.current.velocity = (clientY - dragRef.current.lastY) / deltaTime;
		dragRef.current.lastY = clientY;
		dragRef.current.lastTime = now;
		dragRef.current.offset = nextOffset;
		setDragY(nextOffset);
		return true;
	}, []);

	const finishPointerDrag = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (!dragRef.current.active || dragRef.current.source !== "pointer") return;
			if (event.currentTarget.hasPointerCapture(event.pointerId)) {
				event.currentTarget.releasePointerCapture(event.pointerId);
			}
			settleDrag();
		},
		[settleDrag],
	);

	const handlePointerDown = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (event.pointerType === "touch") return;
			if (event.pointerType === "mouse" && event.button !== 0) return;
			if (!startDrag(event.clientY, event.target, "pointer")) return;
			event.currentTarget.setPointerCapture(event.pointerId);
			event.preventDefault();
		},
		[startDrag],
	);

	const handlePointerMove = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (!updateDrag(event.clientY, "pointer")) return;
			event.preventDefault();
		},
		[updateDrag],
	);

	const handlePointerCancel = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			finishPointerDrag(event);
		},
		[finishPointerDrag],
	);

	const handleTouchStart = useCallback(
		(event: React.TouchEvent<HTMLElement>) => {
			const touch = event.touches[0];
			if (!touch) return;
			if (!startDrag(touch.clientY, event.target, "touch")) return;
			if (event.cancelable) event.preventDefault();
		},
		[startDrag],
	);

	const handleTouchMove = useCallback(
		(event: React.TouchEvent<HTMLElement>) => {
			const touch = event.touches[0];
			if (!touch) return;
			if (!updateDrag(touch.clientY, "touch")) return;
			if (event.cancelable) event.preventDefault();
		},
		[updateDrag],
	);

	const finishTouchDrag = useCallback(
		(event: React.TouchEvent<HTMLElement>) => {
			if (!dragRef.current.active || dragRef.current.source !== "touch") return;
			if (event.cancelable) event.preventDefault();
			settleDrag();
		},
		[settleDrag],
	);

	const handleTouchCancel = useCallback(() => {
		if (!dragRef.current.active || dragRef.current.source !== "touch") return;
		dragRef.current.active = false;
		dragRef.current.source = null;
		dragRef.current.offset = 0;
		setPhase("idle");
		setDragY(0);
	}, []);

	return {
		className: phase === "dragging" ? "is-dragging" : phase === "closing" ? "is-closing" : "",
		handleProps: {
			onPointerCancel: handlePointerCancel,
			onPointerDown: handlePointerDown,
			onPointerMove: handlePointerMove,
			onPointerUp: finishPointerDrag,
			onTouchCancel: handleTouchCancel,
			onTouchEnd: finishTouchDrag,
			onTouchMove: handleTouchMove,
			onTouchStart: handleTouchStart,
		},
		style: { "--sheet-drag-y": `${dragY}px` } as React.CSSProperties,
	};
};

type BottomSheetProps = {
	children: React.ReactNode;
	className?: string;
	headerEnd?: React.ReactNode;
	onClose: () => void;
	onPrimary?: () => Promise<void> | void;
	primaryDisabled?: boolean;
	primaryLabel?: string;
	title: string;
};

const BottomSheet: React.FC<BottomSheetProps> = ({
	children,
	className,
	headerEnd,
	onClose,
	onPrimary,
	primaryDisabled,
	primaryLabel,
	title,
}) => {
	const sheetDrag = useSheetDrag(onClose);

	return (
		<div className="sheet-mask" onClick={onClose}>
			<div
				className={cn("sheet", className, sheetDrag.className)}
				onClick={(event) => event.stopPropagation()}
				style={sheetDrag.style}
			>
				<div className="sheet-grip" {...sheetDrag.handleProps} />
				<div className="sheet-head" {...sheetDrag.handleProps}>
					<button onClick={onClose} type="button">
						取消
					</button>
					<strong>{title}</strong>
					{primaryLabel ? (
						<button
							className="ready"
							disabled={primaryDisabled}
							onClick={() => {
								void onPrimary?.();
							}}
							type="button"
						>
							{primaryLabel}
						</button>
					) : (
						(headerEnd ?? <span />)
					)}
				</div>
				{children}
			</div>
		</div>
	);
};

const useImageAttachment = () => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
	const [imageName, setImageName] = useState("");
	const [imageStatus, setImageStatus] = useState("");

	const openImagePicker = useCallback(() => {
		setImageStatus("请选择相册中的图片");
		inputRef.current?.click();
	}, []);

	const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) {
			setImageStatus("未选择图片");
			return;
		}
		if (!file.type.startsWith("image/")) {
			setImageStatus("请选择图片文件");
			return;
		}
		setImageStatus("正在压缩图片");
		void prepareImageAttachment(file)
			.then((image) => {
				setImageDataUrl(image.dataUrl);
				setImageName(file.name);
				setImageStatus(image.status);
			})
			.catch(() => {
				setImageDataUrl(null);
				setImageName("");
				setImageStatus("图片处理失败，请换一张或裁小一点");
			});
	}, []);

	const input = (
		<input
			accept="image/*"
			aria-hidden="true"
			className="media-input"
			onChange={handleImageChange}
			ref={inputRef}
			tabIndex={-1}
			type="file"
		/>
	);

	return { imageDataUrl, imageName, imageStatus, input, openImagePicker };
};

const useVoiceRecorder = () => {
	const [recording, setRecording] = useState(false);
	const [seconds, setSeconds] = useState(0);
	const [voiceAudioUrl, setVoiceAudioUrl] = useState<string | null>(null);
	const [voiceStatus, setVoiceStatus] = useState<
		"denied" | "idle" | "ready" | "recording" | "unsupported"
	>("idle");
	const chunksRef = useRef<Blob[]>([]);
	const recorderRef = useRef<MediaRecorder | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const voiceAudioUrlRef = useRef<string | null>(null);

	const replaceVoiceAudioUrl = useCallback((url: string | null) => {
		if (voiceAudioUrlRef.current?.startsWith("blob:")) {
			URL.revokeObjectURL(voiceAudioUrlRef.current);
		}
		voiceAudioUrlRef.current = url;
		setVoiceAudioUrl(url);
	}, []);

	const buildVoiceAudioUrl = useCallback(() => {
		if (!chunksRef.current.length) return voiceAudioUrlRef.current;
		const type = recorderRef.current?.mimeType || chunksRef.current[0]?.type || "audio/webm";
		const blob = new Blob(chunksRef.current, { type });
		if (!blob.size) return voiceAudioUrlRef.current;
		const url = URL.createObjectURL(blob);
		replaceVoiceAudioUrl(url);
		return url;
	}, [replaceVoiceAudioUrl]);

	const stopStream = useCallback(() => {
		streamRef.current?.getTracks().forEach((track) => track.stop());
		streamRef.current = null;
	}, []);

	const stopRecording = useCallback(async () => {
		const recorder = recorderRef.current;
		if (recorder && recorder.state !== "inactive") {
			const audioUrl = await new Promise<string | null>((resolve) => {
				const finish = () => {
					recorderRef.current = null;
					const url = buildVoiceAudioUrl();
					resolve(url);
				};
				recorder.addEventListener("stop", finish, { once: true });
				try {
					recorder.requestData();
				} catch {
					// Some browsers do not allow requestData at this point; stop still flushes data.
				}
				recorder.stop();
			});
			stopStream();
			setRecording(false);
			setVoiceStatus("ready");
			return audioUrl;
		}
		if (recorder) {
			recorderRef.current = null;
		}
		stopStream();
		setRecording(false);
		setVoiceStatus((current) => (current === "recording" ? "ready" : current));
		return buildVoiceAudioUrl();
	}, [buildVoiceAudioUrl, stopStream]);

	const startRecording = useCallback(async () => {
		if (recording) return;
		if (!navigator.mediaDevices?.getUserMedia || !("MediaRecorder" in window)) {
			setVoiceStatus("unsupported");
			return;
		}
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			if (voiceAudioUrlRef.current) {
				replaceVoiceAudioUrl(null);
			}
			chunksRef.current = [];
			streamRef.current = stream;
			setSeconds(0);
			setRecording(true);
			setVoiceStatus("recording");
			const recorder = new MediaRecorder(stream);
			recorderRef.current = recorder;
			recorder.addEventListener("dataavailable", (event) => {
				if (!event.data.size) return;
				chunksRef.current.push(event.data);
			});
			recorder.start();
		} catch {
			setRecording(false);
			setVoiceStatus("denied");
			stopStream();
		}
	}, [recording, replaceVoiceAudioUrl, stopStream]);

	const toggleRecording = useCallback(() => {
		if (recording) {
			void stopRecording();
			return;
		}
		void startRecording();
	}, [recording, startRecording, stopRecording]);

	useEffect(() => {
		if (!recording) return undefined;
		const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);
		return () => window.clearInterval(timer);
	}, [recording]);

	useEffect(
		() => () => {
			const recorder = recorderRef.current;
			if (recorder && recorder.state !== "inactive") {
				recorder.stop();
			}
			stopStream();
		},
		[stopStream],
	);

	return {
		recording,
		seconds,
		startRecording,
		stopRecording,
		toggleRecording,
		voiceAudioUrl,
		voiceStatus,
	};
};

const getVoiceStatusText = (
	recording: boolean,
	seconds: number,
	status: "denied" | "idle" | "ready" | "recording" | "unsupported",
) => {
	if (status === "denied") return "没有麦克风权限，请在浏览器设置中允许";
	if (status === "unsupported") return "当前浏览器不支持录音";
	if (recording) return `正在录音 ${seconds}"`;
	if (seconds > 0) return `语音 ${seconds}"`;
	return "点击开始录音";
};

export const RecordSheet: React.FC<{
	onClose: () => void;
	onDone: (practice: Practice, payload: PracticePayload) => void;
	practice: Practice;
}> = ({ onClose, onDone, practice }) => {
	const [text, setText] = useState("");
	const [mode, setMode] = useState<RecordMode>("text");
	const {
		imageDataUrl,
		imageName,
		imageStatus,
		input: imageInput,
		openImagePicker,
	} = useImageAttachment();
	const { recording, seconds, startRecording, stopRecording, toggleRecording, voiceStatus } =
		useVoiceRecorder();
	const closeSheet = useCallback(() => {
		blurActiveControl();
		void stopRecording();
		onClose();
	}, [onClose, stopRecording]);
	const sheetDrag = useSheetDrag(closeSheet);

	const handleMode = useCallback(
		(nextMode: RecordMode) => {
			setMode(nextMode);
			if (nextMode === "image") {
				void stopRecording();
				openImagePicker();
				return;
			}
			if (nextMode === "voice") {
				void startRecording();
				return;
			}
			void stopRecording();
		},
		[openImagePicker, startRecording, stopRecording],
	);

	return (
		<div className="sheet-mask record-sheet-mask" onClick={closeSheet}>
			<div
				className={cn("sheet record-sheet", sheetDrag.className)}
				onClick={(event) => event.stopPropagation()}
				style={sheetDrag.style}
			>
				<div className="sheet-grip" {...sheetDrag.handleProps} />
				<div className="sheet-head" {...sheetDrag.handleProps}>
					<button onClick={closeSheet} type="button">
						取消
					</button>
					<strong>完成练习</strong>
					<button
						className="ready"
						onClick={async () => {
							blurActiveControl();
							const voiceAudio = mode === "voice" ? await stopRecording() : null;
							onDone(practice, {
								mode,
								text: text.trim(),
								image: mode === "image" ? imageDataUrl : null,
								voice: mode === "voice" ? Math.max(seconds, recording ? 1 : 0) : 0,
								voiceAudio,
							});
						}}
						type="button"
					>
						完成
					</button>
				</div>
				<DimChip dim={practice.dim} quiet />
				<h2>{practice.title}</h2>
				<div className="record-prompt-card">
					<span>可以这样写</span>
					<p>{practice.recordPrompt}</p>
				</div>
				<textarea
					onChange={(event) => setText(event.target.value)}
					placeholder="按上面的提示写几句；如果此刻不想写，也可以直接完成。"
					value={text}
				/>
				{imageInput}
				{mode === "image" && (
					<div className={cn("media-preview", imageDataUrl && "has-image")}>
						{imageDataUrl ? (
							<img alt={imageName || "已选择图片"} src={imageDataUrl} />
						) : (
							imageStatus
						)}
					</div>
				)}
				{mode === "voice" && (
					<div className="voice-proof">
						<button
							className={recording ? "recording" : ""}
							onClick={toggleRecording}
							type="button"
						>
							{recording ? <Pause size={18} /> : <Mic size={18} />}
						</button>
						<span>{getVoiceStatusText(recording, seconds, voiceStatus)}</span>
					</div>
				)}
				<ModeTools mode={mode} onMode={handleMode} />
			</div>
		</div>
	);
};

export const NoteSheet: React.FC<{
	groups: GrowthGroup[];
	onClose: () => void;
	onPublish: (payload: PracticePayload) => void;
}> = ({ groups, onClose, onPublish }) => {
	const [text, setText] = useState("");
	const [mode, setMode] = useState<RecordMode>("text");
	const [visibility, setVisibility] = useState<NoteVisibility>("private");
	const [targetGroupId, setTargetGroupId] = useState("");
	const [friendName, setFriendName] = useState("");
	const [friendTag, setFriendTag] = useState(RELATION_TAGS[0]);
	const {
		imageDataUrl,
		imageName,
		imageStatus,
		input: imageInput,
		openImagePicker,
	} = useImageAttachment();
	const { recording, seconds, startRecording, stopRecording, toggleRecording, voiceStatus } =
		useVoiceRecorder();
	const closeSheet = useCallback(() => {
		blurActiveControl();
		void stopRecording();
		onClose();
	}, [onClose, stopRecording]);
	const joinedGroups = groups.filter((group) => group.joined);
	const hasRequiredMedia =
		mode === "image" ? Boolean(imageDataUrl) : mode === "voice" ? recording || seconds > 0 : true;
	const canSave =
		hasRequiredMedia &&
		(visibility === "group"
			? Boolean(targetGroupId)
			: visibility === "friend"
				? Boolean(friendName.trim())
				: true);

	const handleMode = useCallback(
		(nextMode: RecordMode) => {
			setMode(nextMode);
			if (nextMode === "image") {
				void stopRecording();
				openImagePicker();
				return;
			}
			if (nextMode === "voice") {
				void startRecording();
				return;
			}
			void stopRecording();
		},
		[openImagePicker, startRecording, stopRecording],
	);

	return (
		<BottomSheet
			className={cn("note-sheet", `visibility-${visibility}`)}
			onClose={closeSheet}
			onPrimary={async () => {
				blurActiveControl();
				const voiceAudio = mode === "voice" ? await stopRecording() : null;
				onPublish({
					friendName: visibility === "friend" ? friendName.trim() : undefined,
					friendTag: visibility === "friend" ? friendTag : undefined,
					mode,
					text: text.trim(),
					image: mode === "image" ? imageDataUrl : null,
					targetGroupId: visibility === "group" ? targetGroupId : null,
					visibility,
					voice: mode === "voice" ? Math.max(seconds, recording ? 1 : 0) : 0,
					voiceAudio,
				});
			}}
			primaryDisabled={!canSave}
			primaryLabel="保存"
			title="随心记"
		>
			<textarea
				className="note-textarea"
				onChange={(event) => setText(event.target.value)}
				onPointerDown={focusEditableWithoutScroll}
				onTouchStart={focusEditableOnTouchWithoutScroll}
				placeholder="此刻在想什么？随手记下来。"
				value={text}
			/>
			{visibility === "group" && (
				<div className="target-panel">
					{joinedGroups.length ? (
						<div className="group-choice-list">
							{joinedGroups.map((group) => (
								<button
									className={cn(targetGroupId === group.id && "active")}
									key={group.id}
									onClick={() => setTargetGroupId(group.id)}
									type="button"
								>
									<strong>{group.name}</strong>
									<span>{group.sessionTitle}</span>
								</button>
							))}
						</div>
					) : (
						<p>先在“我的”里加入一个小组，再发布给小组成员。</p>
					)}
				</div>
			)}
			{visibility === "friend" && (
				<div className="target-panel friend-target-panel">
					<label className="share-field compact">
						<span>好友昵称</span>
						<input
							onChange={(event) => setFriendName(event.target.value)}
							placeholder="例如：小满"
							value={friendName}
						/>
					</label>
					<div className="relation-tags compact">
						{RELATION_TAGS.map((item) => (
							<button
								className={cn(friendTag === item && "active")}
								key={item}
								onClick={() => setFriendTag(item)}
								type="button"
							>
								{item}
							</button>
						))}
					</div>
				</div>
			)}
			<div className="note-destination">
				<span>保存到</span>
				<div className="destination-tabs">
					<button
						className={cn(visibility === "private" && "active")}
						onClick={() => setVisibility("private")}
						type="button"
					>
						仅自己
					</button>
					<button
						className={cn(visibility === "group" && "active")}
						onClick={() => {
							setVisibility("group");
							setTargetGroupId((value) => value || joinedGroups[0]?.id || "");
						}}
						type="button"
					>
						发到小组
					</button>
					<button
						className={cn(visibility === "friend" && "active")}
						onClick={() => setVisibility("friend")}
						type="button"
					>
						分享给朋友
					</button>
				</div>
			</div>
			{imageInput}
			{mode === "image" && (
				<div className={cn("media-preview", imageDataUrl && "has-image")}>
					{imageDataUrl ? <img alt={imageName || "已选择图片"} src={imageDataUrl} /> : imageStatus}
				</div>
			)}
			{mode === "voice" && (
				<div className="voice-proof">
					<button className={recording ? "recording" : ""} onClick={toggleRecording} type="button">
						{recording ? <Pause size={18} /> : <Mic size={18} />}
					</button>
					<span>{getVoiceStatusText(recording, seconds, voiceStatus)}</span>
				</div>
			)}
			<ModeTools mode={mode} onMode={handleMode} />
		</BottomSheet>
	);
};

const ModeTools: React.FC<{ mode: RecordMode; onMode: (mode: RecordMode) => void }> = ({
	mode,
	onMode,
}) => {
	const tools: Array<{ id: RecordMode; label: string; Icon: LucideIcon }> = [
		{ id: "text", label: "文字", Icon: Type },
		{ id: "image", label: "图片", Icon: ImageIcon },
		{ id: "voice", label: "语音", Icon: Mic },
	];
	return (
		<div className="tool-grid">
			{tools.map(({ Icon, id, label }) => (
				<button
					className={cn("tool-button", mode === id && "active")}
					key={id}
					onClick={() => onMode(id)}
					type="button"
				>
					<Icon className="tool-icon" size={18} />
					<span className="tool-label">{label}</span>
				</button>
			))}
		</div>
	);
};

const SuccessOverlay: React.FC<{
	entry: TimelineItem;
	onClose: () => void;
	onFeedback: (score: number, reason: string) => void;
	onHome: () => void;
	onShare: () => void;
	practice: Practice;
}> = ({ entry, onClose, onFeedback, onHome, onShare, practice }) => {
	return (
		<div className="sheet-mask success-mask">
			<div className="success-card">
				<button className="success-close" onClick={onClose} type="button">
					<X size={18} />
				</button>
				<div className="success-medal">
					<Check size={26} />
				</div>
				<h1>今天这一步完成了</h1>
				<p>「{practice.title}」已经保存到你的个人时间轴。</p>
				<div className="feedback-row">
					<button onClick={() => onFeedback(5, "fit")} type="button">
						适合我
					</button>
					<button onClick={() => onFeedback(3, "neutral")} type="button">
						一般
					</button>
					<button onClick={() => onFeedback(1, "switch")} type="button">
						想换一个
					</button>
				</div>
				<div className="success-preview">
					<TimelineCard item={entry} />
				</div>
				<div className="success-actions">
					<button className="ghost-action" onClick={onHome} type="button">
						回首页继续今日任务
					</button>
					{practice.share && (
						<button className="primary-action" onClick={onShare} type="button">
							分享给好友
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

const ShareSheet: React.FC<{
	entry: TimelineItem;
	onClose: () => void;
	onShared: (asset: { name: string; tag: string; visibility: string }) => void;
}> = ({ entry, onClose, onShared }) => {
	const [name, setName] = useState("");
	const [tag, setTag] = useState(RELATION_TAGS[0]);
	const closeSheet = useCallback(() => {
		blurActiveControl();
		onClose();
	}, [onClose]);
	const downloadPosterText = () => {
		const title = entry.title ?? "今天的真实行动";
		const body = [
			"积极心理学小练习分享卡",
			"",
			title,
			entry.text,
			"",
			name.trim() ? `分享给：${name.trim()}（${tag}）` : "",
		]
			.filter(Boolean)
			.join("\n");
		const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `${title.replace(/[\\/:*?"<>|]/gu, "_")}-分享卡.txt`;
		document.body.append(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
	};
	return (
		<BottomSheet className="share-sheet" onClose={closeSheet} title="分享给微信好友">
			<p className="share-summary">
				生成「{entry.title ?? "练习记录"}」海报后，可以转发给微信好友，并沉淀一条关系记录。
			</p>
			<div className="share-poster">
				<span>练习分享海报</span>
				<strong>{entry.title ?? "今天的真实行动"}</strong>
				<p>{entry.text}</p>
			</div>
			<label className="share-field">
				<span>对方昵称</span>
				<input
					onChange={(event) => setName(event.target.value)}
					placeholder="例如：小满"
					value={name}
				/>
			</label>
			<div className="relation-tags">
				{RELATION_TAGS.map((item) => (
					<button
						className={cn(tag === item && "active")}
						key={item}
						onClick={() => setTag(item)}
						type="button"
					>
						{item}
					</button>
				))}
			</div>
			<button
				className="primary-action full"
				disabled={!name.trim()}
				onClick={() => {
					blurActiveControl();
					downloadPosterText();
					onShared({ name: name.trim(), tag, visibility: "friend" });
				}}
				type="button"
			>
				下载分享卡并记录
			</button>
		</BottomSheet>
	);
};
